import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Nav from "../components/app/Nav";
import SEO from "../components/app/SEO";
import Footer from "../components/app/Footer";
import useForm from "../hooks/useForm";
import usePackageValidation, { validateWeightValue, validateDimensionValue } from "../hooks/usePackageValidation";
import { formatPhoneNational, isValidPhone, getPhonePlaceholder } from "../config/phone.utils";
import { normalizeZip, isValidZipFormat, getZipFormatErrorMessage } from "../config/zip.utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBox,
  faMapMarkerAlt,
  faTruck,
  faCalculator,
  faCreditCard,
  faCheck,
  faChevronRight,
  faChevronLeft,
  faTruckFast,
  faSearch,
  faPlus,
  faSpinner,
  faShieldAlt,
  faPencilAlt,
  faUser,
  faCheckSquare,
  faSquare,
  faCalendarAlt,
  faHandPointer,
} from "@fortawesome/free-solid-svg-icons";
import { tokenService } from "../services/token.service";
import { utilsService, DniTypeObject, applyDniPrefix, getMaxLengthFromMask, getDniCompositeKey, findDniTypeByCompositeKey, getDniLabel } from "../services/utils.service";
import { rateService, RateOption, RatePayload, CalculateExternalRatePayload, ExternalRateProviderResponse, ExternalRateServiceOption } from "../services/rate.service";
import ExternalServiceCard from "../components/common/ExternalServiceCard";
import AdditionalExternalServiceCard from "../components/common/AdditionalExternalServiceCard";
import ServiceCard from "../components/common/ServiceCard";
import OrderSuccessCard from "../components/common/OrderSuccessCard";
import ServiceDetailModalV2 from "../components/common/ServiceDetailModalV2";
import AuthPickupModal from "../components/common/AuthPickupModal";
import EditRawAddressModal from "../components/common/EditRawAddressModal";
import EditPackageDataModal from "../components/common/EditPackageDataModal";
import BoxIllustration from "../components/common/BoxIllustration";
import { authService } from "../services/auth.service";
import { useAuth } from "../context/AuthContext";
import { orderService, CreateOrderRequest } from "../services/order.service";
import { paymentService } from "../services/payment.service";
import { loadStripe } from "@stripe/stripe-js";
import { stripePromise } from "../config/stripe.config";
import { Elements } from "@stripe/react-stripe-js";
import StripePaymentForm from "../components/common/StripePaymentForm";
import Swal from "sweetalert2";
import { errorService } from "../services/error.service";
import { countryService, Country, CountryFull } from "../services/country.service";
import { customerAddressService } from "../services/customer.address.service";
import { fileCompressionService } from "../services/fileCompression.service";
import AddressAutocomplete, {
  AddressComponents,
} from "../components/common/AddressAutocomplete";
import shippingImg from "../assets/app/shipping.png";
import cajaTransparente from "../assets/images/CAJA_TRANSPARENTE.png";
import dropoffStoreImg from "../assets/images/dropoff_store.jpg";
import styles from "../styles/app/PickUpPage.module.css";
import additionalStyles from "../styles/common/AdditionalExternalServiceCard.module.css";

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

const getTomorrowDate = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const day = String(tomorrow.getDate()).padStart(2, '0');
  const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
  const year = tomorrow.getFullYear();
  return `${year}-${month}-${day}`;
};

const getMaxDate = () => {
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 7);
  const day = String(maxDate.getDate()).padStart(2, '0');
  const month = String(maxDate.getMonth() + 1).padStart(2, '0');
  const year = maxDate.getFullYear();
  return `${year}-${month}-${day}`;
};


// Initialize Stripe outside component

const PickUpPage: React.FC = () => {
  const getCountryName = (code: string) => {
    if (!code) return "";
    const uppercaseCode = code.toUpperCase();
    const found = allCountries.find((c) => c.iso2Country?.toUpperCase() === uppercaseCode)
      || senderCountries.find((c) => c.iso2Country?.toUpperCase() === uppercaseCode)
      || receiverCountries.find((c) => c.iso2Country?.toUpperCase() === uppercaseCode);
    return found ? found.name : code;
  };

  const { syncToken, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isValidatingZip, setIsValidatingZip] = useState(false);
  const [isValidatingEmail, setIsValidatingEmail] = useState(false);
  const [emailExists, setEmailExists] = useState<boolean | null>(null);
  const [existingCustomerName, setExistingCustomerName] = useState<string>("");
  const [zipValidatedByField, setZipValidatedByField] = useState({
    originZip: false,
    destinationZip: false,
  });
  const [isAuthForPickUp, setIsAuthForPickUp] = useState<boolean>(() => {
    return !!tokenService.getCustomerDataToken();
  });
  const [disclaimerChecked, setDisclaimerChecked] = useState(false);
  const [disclaimerError, setDisclaimerError] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<{ file: File; base64string: string; ext: string } | null>(null);
  const [docErrors, setDocErrors] = useState<string>("");
  const [isCompressingDoc, setIsCompressingDoc] = useState<boolean>(false);

  const customerDataFromToken =
    tokenService.getCustomerDataToken()?.customerData;

  const handleDocumentChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const file = e.target.files[0];
    if (!file) return;

    const allowedExtensions = ["png", "jpg", "jpeg", "pdf"];
    const fileExt = file.name.split('.').pop()?.toLowerCase() || "";
    if (!allowedExtensions.includes(fileExt)) {
      setDocErrors(`El archivo "${file.name}" tiene una extensión no permitida. Solo se aceptan png, jpg, jpeg, pdf.`);
      return;
    }

    const maxSizeBytes = fileExt === "pdf" ? 500 * 1024 : 5 * 1024 * 1024;
    const maxWeightLabel = fileExt === "pdf" ? "500KB" : "5MB";

    if (file.size > maxSizeBytes) {
      setDocErrors(`El archivo "${file.name}" supera el peso máximo de ${maxWeightLabel}.`);
      return;
    }

    try {
      setIsCompressingDoc(true);
      setDocErrors("");

      const result = await fileCompressionService.compressFile(file, {
        maxWidth: 1920,
        maxHeight: 1920,
        quality: 0.8,
      });

      setSelectedDocument({
        file: result.file,
        base64string: result.base64string,
        ext: result.ext,
      });
    } catch (err) {
      console.error("Error al procesar/comprimir el archivo:", err);
      setDocErrors("Error al procesar el archivo.");
    } finally {
      setIsCompressingDoc(false);
      e.target.value = "";
    }
  };

  const removeDocument = () => {
    setSelectedDocument(null);
    setDocErrors("");
  };

  const [senderDniTypes, setSenderDniTypes] = useState<DniTypeObject[]>([]);
  const [receiverDniTypes, setReceiverDniTypes] = useState<DniTypeObject[]>([]);
  const [customerOriginAddresses, setCustomerOriginAddresses] = useState<any[]>([]);
  const [selectedOriginAddressId, setSelectedOriginAddressId] = useState<string | number>("custom");
  const [allSavedAddresses, setAllSavedAddresses] = useState<any[]>([]);
  const [selectedDestAddressId, setSelectedDestAddressId] = useState<string | number>("custom");

  const initialValues = {
    origin: "US",
    destination: "",
    originZip: "",
    destinationZip: "",
    weight: "" as number | "",
    width: "" as number | "",
    height: "" as number | "",
    length: "" as number | "",
    fob: "" as number | "",
    unitSystem: "imperial" as "metric" | "imperial",
    currency: "USD",
    parcelOrdDoc: "PARCEL",
    serviceType: "A",
    selectedService: "",
    packageContent: "",
    idCarrier: 0,
    idCarrierService: undefined as number | undefined,
    hasBattery: false,
    // Sender details
    senderFirstName: customerDataFromToken?.FirstName || "",
    senderLastName: customerDataFromToken?.LastName || "",
    senderEmail: customerDataFromToken?.Email || "",
    senderPhone: customerDataFromToken?.PhoneNumber ? customerDataFromToken.PhoneNumber.replace(/^\+1/, "") : "",
    senderKindOfDNI: customerDataFromToken?.KindOfDNI || "DOC",
    senderDNI: customerDataFromToken?.DNI || "",
    senderAddressLine1: "",
    senderAddressLine2: "",
    senderCity: "",
    senderState: "",
    senderRazonSocial: "",
    pickupDate: getTomorrowDate(),
    // Receiver details
    receiverFirstName: "",
    receiverLastName: "",
    receiverEmail: "",
    receiverPhone: "",
    receiverKindOfDNI: "",
    receiverDNI: "",
    receiverRazonSocial: "",
    receiverAddressLine1: "",
    receiverAddressLine2: "",
    receiverCity: "",
    receiverState: "",
    municipalityOrigin: "",
    municipalityDestiny: "",
    isThirdPerson: false,
    userFirstName: "",
    userLastName: "",
    userEmail: "",
    userCountry: "",
  };

  const validationRules = {
    userFirstName: (value: string, allValues: any) => {
      if (allValues.isThirdPerson) {
        if (!value) return "El nombre del usuario es requerido";
        if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(value)) {
          return "El nombre no debe contener números ni caracteres especiales";
        }
      }
      return null;
    },
    userLastName: (value: string, allValues: any) => {
      if (allValues.isThirdPerson) {
        if (!value) return "El apellido del usuario es requerido";
        if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(value)) {
          return "El apellido no debe contener números ni caracteres especiales";
        }
      }
      return null;
    },
    userEmail: (value: string, allValues: any) => {
      if (allValues.isThirdPerson) {
        if (!value) return "El email del usuario es requerido";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "El formato del email no es válido";
      }
      return null;
    },
    userCountry: (value: string, allValues: any) =>
      allValues.isThirdPerson && !value
        ? "El país del usuario es requerido"
        : null,
    weight: (value: number | "", allValues: any) =>
      validateWeightValue(value, allValues?.unitSystem),
    width: (value: number | "", allValues: any) =>
      validateDimensionValue(value, "Ancho", allValues?.unitSystem),
    height: (value: number | "", allValues: any) =>
      validateDimensionValue(value, "Alto", allValues?.unitSystem),
    length: (value: number | "", allValues: any) =>
      validateDimensionValue(value, "Largo", allValues?.unitSystem),
    fob: (value: number | "") =>
      !value || value <= 0 ? "El valor FOB debe ser mayor a 0" : null,
    packageContent: (value: string) =>
      !value ? "La descripción del contenido es requerida" : null,
    // Sender validation
    senderFirstName: (value: string) =>
      !value
        ? "El nombre es requerido"
        : !/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(value)
          ? "El nombre no debe contener números ni caracteres especiales"
          : null,
    senderLastName: (value: string) =>
      !value
        ? "El apellido es requerido"
        : !/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(value)
          ? "El apellido no debe contener números ni caracteres especiales"
          : null,
    senderEmail: (value: string) =>
      !value
        ? "El email es requerido"
        : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
          ? "El formato del email no es válido"
          : null,
    senderPhone: (value: string, allValues: any) => {
      if (!value) return "El teléfono es requerido";
      if (!isValidPhone(value, allValues.origin)) return "Número de teléfono inválido";
      return null;
    },
    senderKindOfDNI: (value: string) => (!value ? "Tipo doc. requerido" : null),
    senderDNI: (value: string, allValues: any) => {
      if (!value) return "Documento requerido";
      const selectedDniObj = findDniTypeByCompositeKey(senderDniTypes, allValues.senderKindOfDNI);
      if (selectedDniObj && selectedDniObj.mask) {
        const regex = new RegExp(selectedDniObj.mask);
        if (!regex.test(value)) {
          return `El documento no coincide con el formato requerido para ${selectedDniObj.kindOfDNI}.`;
        }
      }
      return null;
    },
    senderRazonSocial: (value: string, allValues: any) => {
      const selectedDniObj = findDniTypeByCompositeKey(senderDniTypes, allValues.senderKindOfDNI);
      if (selectedDniObj?.requiereRazonSocial && !value) {
        return "La razón social es requerida";
      }
      return null;
    },
    senderAddressLine1: (value: string) =>
      !value ? "La dirección es requerida" : null,
    senderCity: (value: string) => (!value ? "La ciudad es requerida" : null),
    senderState: (value: string) => (!value ? "El estado es requerido" : null),
    originZip: (value: string) =>
      !value ? "El código postal es requerido" : null,
    // Receiver validation
    receiverFirstName: (value: string) =>
      !value
        ? "El nombre es requerido"
        : !/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(value)
          ? "El nombre no debe contener números ni caracteres especiales"
          : null,
    receiverLastName: (value: string) =>
      !value
        ? "El apellido es requerido"
        : !/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(value)
          ? "El apellido no debe contener números ni caracteres especiales"
          : null,
    receiverEmail: (value: string) =>
      !value
        ? "El email es requerido"
        : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
          ? "El formato del email no es válido"
          : null,
    receiverPhone: (value: string, allValues: any) => {
      if (!value) return "El teléfono es requerido";
      if (!isValidPhone(value, allValues.destination)) return "Número de teléfono inválido";
      return null;
    },
    receiverKindOfDNI: (value: string) =>
      !value ? "Tipo doc. requerido" : null,
    receiverDNI: (value: string, allValues: any) => {
      if (!value) return "Documento requerido";
      const selectedDniObj = findDniTypeByCompositeKey(receiverDniTypes, allValues.receiverKindOfDNI);
      if (selectedDniObj && selectedDniObj.mask) {
        const regex = new RegExp(selectedDniObj.mask);
        if (!regex.test(value)) {
          return `El documento no coincide con el formato requerido para ${selectedDniObj.kindOfDNI}.`;
        }
      }
      return null;
    },
    receiverAddressLine1: (value: string) =>
      !value ? "La dirección es requerida" : null,
    receiverCity: (value: string) => (!value ? "La ciudad es requerida" : null),
    receiverState: (value: string) =>
      !value ? "El estado es requerido" : null,
    destinationZip: (value: string) =>
      !value ? "El código postal es requerido" : null,
  };

  const { values, errors, handleChange, setValues, setErrors, validate } =
    useForm(initialValues, validationRules);



  const handleSenderPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const formatted = formatPhoneNational(val, values.origin, selectedSenderCountry?.phoneCode);
    setValues((prev) => ({ ...prev, senderPhone: formatted }));

    const isVal = isValidPhone(formatted, values.origin);
    setErrors((prev: any) => ({
      ...prev,
      senderPhone: !formatted ? "El teléfono es requerido" : !isVal ? "Número de teléfono inválido" : null,
    }));
  };

  const handleReceiverPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const formatted = formatPhoneNational(val, values.destination, selectedReceiverCountry?.phoneCode);
    setValues((prev) => ({ ...prev, receiverPhone: formatted }));

    const isVal = isValidPhone(formatted, values.destination);
    setErrors((prev: any) => ({
      ...prev,
      receiverPhone: !formatted ? "El teléfono es requerido" : !isVal ? "Número de teléfono inválido" : null,
    }));
  };

  const handleSenderDniChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const selectedDniObj = findDniTypeByCompositeKey(senderDniTypes, values.senderKindOfDNI);
    const formatted = applyDniPrefix(val, selectedDniObj?.mask);
    setValues((prev) => ({ ...prev, senderDNI: formatted }));

    const error = validationRules.senderDNI(formatted, { ...values, senderDNI: formatted });
    setErrors((prev: any) => ({
      ...prev,
      senderDNI: error,
    }));
  };

  const handleReceiverDniChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const selectedDniObj = findDniTypeByCompositeKey(receiverDniTypes, values.receiverKindOfDNI);
    const formatted = applyDniPrefix(val, selectedDniObj?.mask);
    setValues((prev) => ({ ...prev, receiverDNI: formatted }));

    const error = validationRules.receiverDNI(formatted, { ...values, receiverDNI: formatted });
    setErrors((prev: any) => ({
      ...prev,
      receiverDNI: error,
    }));
  };

  const handleSenderKindOfDniChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    setValues((prev) => ({
      ...prev,
      senderKindOfDNI: val,
      senderDNI: "",
    }));
    setErrors((prev: any) => ({
      ...prev,
      senderKindOfDNI: null,
      senderDNI: null,
    }));
  };

  const handleReceiverKindOfDniChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    setValues((prev) => ({
      ...prev,
      receiverKindOfDNI: val,
      receiverDNI: "",
    }));
    setErrors((prev: any) => ({
      ...prev,
      receiverKindOfDNI: null,
      receiverDNI: null,
    }));
  };

  const [senderCountries, setSenderCountries] = useState<Country[]>([]);
  const [receiverCountries, setReceiverCountries] = useState<Country[]>([]);
  const [allCountries, setAllCountries] = useState<CountryFull[]>([]);

  // Derived: currently selected sender country object (for phoneCode)
  const selectedSenderCountry = React.useMemo(
    () => senderCountries.find((c) => c.iso2Country === values.origin) ?? null,
    [senderCountries, values.origin]
  );

  // Derived: currently selected receiver country object (for flag & phoneCode)
  const selectedReceiverCountry = React.useMemo(
    () => receiverCountries.find((c) => c.iso2Country === values.destination) ?? null,
    [receiverCountries, values.destination]
  );
  const [rateResults, setRateResults] = useState<RateOption[]>([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [
    selectedExternalIndicesByCarrier,
    setSelectedExternalIndicesByCarrier,
  ] = useState<Map<string, Set<number>>>(new Map());

  // External rate state
  const [externalServices, setExternalServices] = useState<ExternalRateProviderResponse[]>([]);
  const [selectedExternalOption, setSelectedExternalOption] = useState<ExternalRateServiceOption | null>(null);
  const [pickupSelected, setPickupSelected] = useState<boolean>(false);
  const [elpaketDispatcherSelected, setElpaketDispatcherSelected] = useState<boolean>(false);
  const userWantsPickupRef = React.useRef<boolean>(false);
  const userWantsElpaketDispatcherRef = React.useRef<boolean>(false);
  const lastCalculatedPayloadRef = React.useRef<string>("");
  const [isPreferenceInitialized, setIsPreferenceInitialized] = useState<boolean>(false);
  const [loadingExternal, setLoadingExternal] = useState<boolean>(false);
  const [showServiceDetailModal, setShowServiceDetailModal] = useState(false);
  const [selectedRateForModal, setSelectedRateForModal] =
    useState<RateOption | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showEditPackageModal, setShowEditPackageModal] = useState(false);
  const [showEditSenderModal, setShowEditSenderModal] = useState(false);
  const [showEditReceiverModal, setShowEditReceiverModal] = useState(false);
  const [currencies, setCurrencies] = useState<any[]>([]);

  // Payment and Order State
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [allowRedirect, setAllowRedirect] = useState(false);
  const [orderSuccessData, setOrderSuccessData] = useState<any | null>(null);
  const [stripeError, setStripeError] = useState(false);

  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      if (allowRedirect || currentStep >= 6 || orderSuccessData || isProcessingOrder) return;
      if (!(currentStep > 1 || values.senderFirstName !== "" || values.receiverFirstName !== "" || values.packageContent !== "" || Boolean(values.weight))) return;

      let target = e.target as HTMLElement | null;
      while (target && target.tagName !== "A") {
        target = target.parentElement;
      }

      if (target) {
        const href = target.getAttribute("href");
        const isLogout = target.textContent?.includes("Salir") || target.innerText?.includes("Salir");

        if (isLogout || (href && (href.startsWith("/") || href.startsWith(window.location.origin)))) {
          e.preventDefault();
          e.stopPropagation();

          Swal.fire({
            title: "¿Estás seguro de que deseas salir?",
            text: "Si sales ahora perderás toda la información ingresada para esta recogida.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#ffc114",
            cancelButtonColor: "#64748b",
            confirmButtonText: "Sí, salir",
            cancelButtonText: "Cancelar"
          }).then(async (result) => {
            if (result.isConfirmed) {
              setAllowRedirect(true);
              if (isLogout) {
                await logout();
              } else if (href) {
                navigate(href);
              }
            }
          });
        }
      }
    };

    document.addEventListener("click", handleAnchorClick, true);
    return () => {
      document.removeEventListener("click", handleAnchorClick, true);
    };
  }, [allowRedirect, currentStep, orderSuccessData, isProcessingOrder, values.senderFirstName, values.receiverFirstName, values.packageContent, values.weight, navigate, logout]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!allowRedirect && currentStep < 6 && !orderSuccessData && !isProcessingOrder && (currentStep > 1 || values.senderFirstName !== "" || values.receiverFirstName !== "" || values.packageContent !== "" || Boolean(values.weight))) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [allowRedirect, currentStep, orderSuccessData, isProcessingOrder, values.senderFirstName, values.receiverFirstName, values.packageContent, values.weight]);

  // Google Places Autocomplete
  const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
  const [senderAddress, setSenderAddress] = useState("");
  const [receiverAddress, setReceiverAddress] = useState("");

  const extractAddressComponents = useCallback(
    (place: google.maps.places.PlaceResult) => {
      let city = "";
      let state = "";
      let zipCode = "";
      let country = "US";

      if (place.address_components) {
        for (const component of place.address_components) {
          const types = component.types;
          if (types.includes("locality")) {
            city = component.long_name;
          } else if (types.includes("administrative_area_level_1")) {
            state = component.long_name;
          } else if (types.includes("postal_code")) {
            zipCode = component.long_name;
          } else if (types.includes("country")) {
            country = component.short_name;
          }
        }
      }

      return {
        city,
        state,
        zipCode,
        country,
        fullAddress: place.formatted_address || "",
      };
    },
    [],
  );

  const handleSenderPlaceSelected = useCallback(
    (place: google.maps.places.PlaceResult) => {
      const addressData = extractAddressComponents(place);
      setValues((prev: any) => ({
        ...prev,
        senderAddressLine1: addressData.fullAddress,
        senderCity: addressData.city,
        senderState: addressData.state,
        originZip: addressData.zipCode,
      }));
      if (addressData.zipCode) {
        setZipValidatedByField((prev) => ({ ...prev, originZip: true }));
      }
    },
    [extractAddressComponents, setValues],
  );

  const handleReceiverPlaceSelected = useCallback(
    (place: google.maps.places.PlaceResult) => {
      const addressData = extractAddressComponents(place);
      setValues((prev: any) => ({
        ...prev,
        receiverAddressLine1: addressData.fullAddress,
        receiverCity: addressData.city,
        receiverState: addressData.state,
        destinationZip: addressData.zipCode,
      }));
      if (addressData.zipCode) {
        setZipValidatedByField((prev) => ({ ...prev, destinationZip: true }));
      }
    },
    [extractAddressComponents, setValues],
  );
  const validateZipCode = useCallback(
    async (
      field: "originZip" | "destinationZip",
      zipToValidate?: string,
    ) => {
      let rawZip = zipToValidate || values[field];
      if (!rawZip) return;
      const country = field === "originZip" ? (values.origin || "US") : (values.destination || "US");
      const zip = normalizeZip(rawZip, country);

      setValues((prev: any) => ({ ...prev, [field]: zip }));

      if (!isValidZipFormat(zip, country)) {
        setErrors((prev: any) => ({
          ...prev,
          [field]: getZipFormatErrorMessage(country),
        }));
        setZipValidatedByField((prev) => ({ ...prev, [field]: false }));
        return;
      }

      // Valid format: clear errors & mark validated
      setErrors((prev: any) => ({ ...prev, [field]: null }));
      setZipValidatedByField((prev) => ({ ...prev, [field]: true }));
      setIsValidatingZip(true);

      try {
        const location = await utilsService.getLocationByZipcode({
          iso2code: country,
          zipcode: zip,
        });

        if (location) {
          // Auto-fill city and state if available
          const cityField = field === "originZip" ? "senderCity" : "receiverCity";
          const stateField =
            field === "originZip" ? "senderState" : "receiverState";
          setValues((prev: any) => {
            const newCity = prev[cityField] || location.city;
            const newState = prev[stateField] || location.state;
            setErrors((prevErrors: any) => ({
              ...prevErrors,
              [cityField]: newCity ? undefined : prevErrors[cityField],
              [stateField]: newState ? undefined : prevErrors[stateField],
            }));
            return {
              ...prev,
              [cityField]: newCity,
              [stateField]: newState,
            };
          });
        }
      } catch (error) {
        console.warn("Non-blocking zip lookup notice:", error);
      } finally {
        setIsValidatingZip(false);
      }
    },
    [values.origin, values.destination, values.originZip, values.destinationZip, setValues, setErrors],
  );

  const handleSenderAddressSelect = useCallback(
    (addressData: AddressComponents) => {
      const country = values.origin || "US";
      const normalizedZip = normalizeZip(addressData.zipCode || "", country);
      setValues((prev: any) => ({
        ...prev,
        senderAddressLine1: addressData.fullAddress,
        senderCity: addressData.city || prev.senderCity,
        senderState: addressData.state || prev.senderState,
        municipalityOrigin: addressData.municipality || prev.municipalityOrigin,
        originZip: normalizedZip || prev.originZip,
      }));
      setErrors((prev: any) => ({
        ...prev,
        senderAddressLine1: undefined,
        senderCity: undefined,
        senderState: undefined,
        originZip: undefined,
      }));
      if (normalizedZip) {
        setZipValidatedByField((prev) => ({ ...prev, originZip: true }));
      }
    },
    [values.origin, setValues, setErrors],
  );

  const handleReceiverAddressSelect = useCallback(
    (addressData: AddressComponents) => {
      const country = values.destination || "US";
      const normalizedZip = normalizeZip(addressData.zipCode || "", country);
      setValues((prev: any) => ({
        ...prev,
        receiverAddressLine1: addressData.fullAddress,
        receiverCity: addressData.city || prev.receiverCity,
        receiverState: addressData.state || prev.receiverState,
        municipalityDestiny: addressData.municipality || prev.municipalityDestiny,
        destinationZip: normalizedZip || prev.destinationZip,
      }));
      setErrors((prev: any) => ({
        ...prev,
        receiverAddressLine1: undefined,
        receiverCity: undefined,
        receiverState: undefined,
        destinationZip: undefined,
      }));
      if (normalizedZip) {
        setZipValidatedByField((prev) => ({ ...prev, destinationZip: true }));
      }
    },
    [values.destination, setValues, setErrors],
  );

  const getFormattedAddressLabel = (addr: any) => {
    const star = addr.isDefault ? "⭐ " : "";
    const customAlias =
      addr.nickname &&
      !["origen", "destino", "origin", "destination"].includes(addr.nickname.trim().toLowerCase()) &&
      addr.nickname.trim().toLowerCase() !== addr.addressLine1.trim().toLowerCase()
        ? `${addr.nickname}: `
        : "";
    const cityPart =
      addr.city && !addr.addressLine1.toLowerCase().includes(addr.city.toLowerCase())
        ? `, ${addr.city}`
        : "";
    const zipPart =
      addr.zipCode && !addr.addressLine1.includes(addr.zipCode)
        ? ` (${addr.zipCode})`
        : "";
    return `${star}${customAlias}${addr.addressLine1}${cityPart}${zipPart}`;
  };

  const availableDestAddresses = React.useMemo(() => {
    if (!values.destination || !Array.isArray(allSavedAddresses)) return [];
    return allSavedAddresses
      .filter(
        (a: any) =>
          (a.isO2Country || "").toUpperCase() === (values.destination || "").toUpperCase() &&
          (a.type === "DESTINATION" || a.type === "D")
      )
      .sort((a: any, b: any) => {
        if (b.isDefault && !a.isDefault) return 1;
        if (!b.isDefault && a.isDefault) return -1;
        return (b.id || 0) - (a.id || 0);
      });
  }, [allSavedAddresses, values.destination]);

  const handleOriginAddressChange = (addressId: string | number) => {
    setSelectedOriginAddressId(addressId);
    if (addressId === "custom") {
      setValues((prev: any) => ({
        ...prev,
        senderAddressLine1: "",
        senderAddressLine2: "",
        senderCity: "",
        senderState: "",
        originZip: "",
      }));
      setZipValidatedByField((prev) => ({ ...prev, originZip: false }));
      return;
    }

    const selectedAddr = customerOriginAddresses.find((a: any) => String(a.id) === String(addressId));
    if (!selectedAddr) return;

    setValues((prev: any) => {
      const updates: any = {
        senderAddressLine1: selectedAddr.addressLine1 || "",
        senderAddressLine2: selectedAddr.addressLine2 || "",
        senderCity: selectedAddr.city || "",
        senderState: selectedAddr.state || "",
        originZip: selectedAddr.zipCode || "",
      };

      if (selectedAddr.addressSubjet) {
        const subj = selectedAddr.addressSubjet;
        if (subj.firstName) updates.senderFirstName = subj.firstName;
        if (subj.lastName) updates.senderLastName = subj.lastName;
        if (subj.email) updates.senderEmail = subj.email;
        if (subj.phoneNumber) updates.senderPhone = subj.phoneNumber.replace(/^\+1/, "");
        if (subj.dni) updates.senderDNI = subj.dni;
        if (subj.kindOfDNI) {
          const matchedDni = senderDniTypes.find(
            (t) => t.kindOfDNI.toLowerCase() === subj.kindOfDNI.toLowerCase() || getDniCompositeKey(t) === subj.kindOfDNI
          );
          if (matchedDni) {
            updates.senderKindOfDNI = getDniCompositeKey(matchedDni);
          } else {
            updates.senderKindOfDNI = subj.kindOfDNI;
          }
        }
        if (subj.razonSocial) updates.senderRazonSocial = subj.razonSocial;
      }

      return { ...prev, ...updates };
    });

    setErrors((prev: any) => ({
      ...prev,
      senderAddressLine1: undefined,
      senderCity: undefined,
      senderState: undefined,
      originZip: undefined,
    }));

    if (selectedAddr.zipCode) {
      setZipValidatedByField((prev) => ({ ...prev, originZip: true }));
    }
  };

  const handleDestAddressChange = (addressId: string | number) => {
    setSelectedDestAddressId(addressId);
    if (addressId === "custom") {
      setValues((prev: any) => ({
        ...prev,
        receiverFirstName: "",
        receiverLastName: "",
        receiverEmail: "",
        receiverPhone: "",
        receiverKindOfDNI: "",
        receiverDNI: "",
        receiverRazonSocial: "",
        receiverAddressLine1: "",
        receiverAddressLine2: "",
        receiverCity: "",
        receiverState: "",
        destinationZip: "",
        municipalityDestiny: "",
      }));
      setZipValidatedByField((prev) => ({ ...prev, destinationZip: false }));
      return;
    }

    const selectedAddr = availableDestAddresses.find((a: any) => String(a.id) === String(addressId));
    if (!selectedAddr) return;

    setValues((prev: any) => {
      const updates: any = {
        receiverAddressLine1: selectedAddr.addressLine1 || "",
        receiverAddressLine2: selectedAddr.addressLine2 || "",
        receiverCity: selectedAddr.city || "",
        receiverState: selectedAddr.state || "",
        destinationZip: selectedAddr.zipCode || "",
      };

      if (selectedAddr.addressSubjet) {
        const subj = selectedAddr.addressSubjet;
        if (subj.firstName) updates.receiverFirstName = subj.firstName;
        if (subj.lastName) updates.receiverLastName = subj.lastName;
        if (subj.email) updates.receiverEmail = subj.email;
        if (subj.phoneNumber) {
          const phoneCountry = receiverCountries.find((c) => c.iso2Country === values.destination);
          const prefix = phoneCountry?.phoneCode || "";
          updates.receiverPhone = subj.phoneNumber.startsWith(prefix) ? subj.phoneNumber.slice(prefix.length) : subj.phoneNumber;
        }
        if (subj.dni) updates.receiverDNI = subj.dni;
        if (subj.kindOfDNI) {
          const matchedDni = receiverDniTypes.find(
            (t) => t.kindOfDNI.toLowerCase() === subj.kindOfDNI.toLowerCase() || getDniCompositeKey(t) === subj.kindOfDNI
          );
          if (matchedDni) {
            updates.receiverKindOfDNI = getDniCompositeKey(matchedDni);
          } else {
            updates.receiverKindOfDNI = subj.kindOfDNI;
          }
        }
        if (subj.razonSocial) updates.receiverRazonSocial = subj.razonSocial;
      }

      return { ...prev, ...updates };
    });

    setErrors((prev: any) => ({
      ...prev,
      receiverAddressLine1: undefined,
      receiverCity: undefined,
      receiverState: undefined,
      destinationZip: undefined,
    }));

    if (selectedAddr.zipCode) {
      setZipValidatedByField((prev) => ({ ...prev, destinationZip: true }));
    }
  };

  useEffect(() => {
    if (!values.destination || availableDestAddresses.length === 0) {
      setSelectedDestAddressId("custom");
      return;
    }
    const defaultDest = availableDestAddresses.find((a: any) => a.isDefault) || availableDestAddresses[0];
    if (defaultDest && selectedDestAddressId === "custom") {
      handleDestAddressChange(defaultDest.id);
    }
  }, [values.destination, availableDestAddresses]);

  useEffect(() => {
    stripePromise.then((stripe) => {
      if (!stripe) {
        setStripeError(true);
      }
    });
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStep]);

  useEffect(() => {
    const fetchSenderDniTypes = async () => {
      try {
        const senderData = await utilsService.getDniTypes("US");
        setSenderDniTypes(senderData);

        const currentCustomer = tokenService.getCustomerDataToken()?.customerData;
        if (currentCustomer) {
          setValues((prev: any) => {
            const updates: any = {};
            if (!prev.senderFirstName && currentCustomer.FirstName) updates.senderFirstName = currentCustomer.FirstName;
            if (!prev.senderLastName && currentCustomer.LastName) updates.senderLastName = currentCustomer.LastName;
            if (!prev.senderEmail && currentCustomer.Email) updates.senderEmail = currentCustomer.Email;
            if (!prev.senderPhone && currentCustomer.PhoneNumber) {
              updates.senderPhone = currentCustomer.PhoneNumber.replace(/^\+1/, "");
            }
            if (currentCustomer.KindOfDNI) {
              const matched = findDniTypeByCompositeKey(senderData, currentCustomer.KindOfDNI) ||
                senderData.find(t => t.kindOfDNI.toLowerCase() === currentCustomer.KindOfDNI.toLowerCase());
              if (matched) {
                updates.senderKindOfDNI = getDniCompositeKey(matched);
              } else if (!prev.senderKindOfDNI || prev.senderKindOfDNI === "DOC") {
                updates.senderKindOfDNI = currentCustomer.KindOfDNI;
              }
            } else if (senderData.length > 0 && (!prev.senderKindOfDNI || prev.senderKindOfDNI === "DOC")) {
              updates.senderKindOfDNI = getDniCompositeKey(senderData[0]);
            }
            if (!prev.senderDNI && currentCustomer.DNI) {
              updates.senderDNI = currentCustomer.DNI;
            }
            return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
          });
        } else if (senderData.length > 0) {
          setValues((prev: any) => {
            if (!prev.senderKindOfDNI || prev.senderKindOfDNI === "DOC") {
              return { ...prev, senderKindOfDNI: getDniCompositeKey(senderData[0]) };
            }
            return prev;
          });
        }
      } catch (error) {
        console.error("Error fetching DNI types:", error);
      }
    };
    fetchSenderDniTypes();
  }, []);

  useEffect(() => {
    const fetchCustomerAddresses = async () => {
      const token = tokenService.getToken();
      if (!token) return;
      try {
        const addressData = await customerAddressService.getAddresses();
        const allAddrs = addressData.all || [];
        setAllSavedAddresses(allAddrs);

        const rawOrigins = (addressData.origins && addressData.origins.length > 0 && addressData.origins[0].length > 0)
          ? addressData.origins[0]
          : allAddrs.filter((a: any) => a.type === "ORIGIN" || a.type === "O" || a.isO2Country === "US");
        
        const sortedOrigins = [...rawOrigins].sort((a: any, b: any) => {
          if (b.isDefault && !a.isDefault) return 1;
          if (!b.isDefault && a.isDefault) return -1;
          return (b.id || 0) - (a.id || 0);
        });

        setCustomerOriginAddresses(sortedOrigins);

        if (sortedOrigins.length > 0) {
          const defaultOrigin = sortedOrigins.find((o: any) => o.isDefault) || sortedOrigins[0];
          setSelectedOriginAddressId(defaultOrigin.id || "custom");

          setValues((prev: any) => {
            const updates: any = {
              senderAddressLine1: defaultOrigin.addressLine1 || "",
              senderAddressLine2: defaultOrigin.addressLine2 || "",
              senderCity: defaultOrigin.city || "",
              senderState: defaultOrigin.state || "",
              originZip: defaultOrigin.zipCode || "",
            };
            if (defaultOrigin.addressSubjet) {
              const subj = defaultOrigin.addressSubjet;
              if (subj.firstName) updates.senderFirstName = subj.firstName;
              if (subj.lastName) updates.senderLastName = subj.lastName;
              if (subj.email) updates.senderEmail = subj.email;
              if (subj.phoneNumber) updates.senderPhone = subj.phoneNumber.replace(/^\+1/, "");
              if (subj.dni) updates.senderDNI = subj.dni;
              if (subj.kindOfDNI) updates.senderKindOfDNI = subj.kindOfDNI;
              if (subj.razonSocial) updates.senderRazonSocial = subj.razonSocial;
            }
            return { ...prev, ...updates };
          });
          if (defaultOrigin.zipCode) {
            setZipValidatedByField((prev) => ({ ...prev, originZip: true }));
          }
        }

        // Seleccionar país de destino del último envío del cliente si existe
        try {
          const ordersRes = await orderService.getCustomerOrders({ page: 1, pageSize: 1 });
          if (ordersRes && ordersRes.data && ordersRes.data.length > 0) {
            const lastOrder = ordersRes.data[0];
            if (lastOrder.isoDestiny) {
              const lastCountryIso = lastOrder.isoDestiny.toUpperCase();
              setValues((prev: any) => {
                if (!prev.destination) {
                  return { ...prev, destination: lastCountryIso };
                }
                return prev;
              });
            }
          } else {
            // Fallback: si no tiene envíos previos, verificar si tiene alguna dirección de destino guardada
            const destAddrs = allAddrs.filter((a: any) => a.type === "DESTINATION" || a.type === "D");
            const defaultDest = destAddrs.find((a: any) => a.isDefault) || destAddrs[0];
            if (defaultDest && defaultDest.isO2Country) {
              const defaultDestCountryIso = defaultDest.isO2Country.toUpperCase();
              setValues((prev: any) => {
                if (!prev.destination) {
                  return { ...prev, destination: defaultDestCountryIso };
                }
                return prev;
              });
            }
          }
        } catch (orderErr) {
          console.error("Error fetching customer last order for destination country:", orderErr);
        }
      } catch (e) {
        console.error("Error fetching customer addresses:", e);
      }
    };
    fetchCustomerAddresses();
  }, []);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const { senders, receivers } = await countryService.getCountryLists();
        setSenderCountries(senders);
        setReceiverCountries(receivers);

        const all = await countryService.getAllCountries();
        setAllCountries(all);
      } catch (error) {
        console.error("Error fetching country lists:", error);
      }
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    if (values.parcelOrdDoc === "DOC" && values.hasBattery) {
      setValues((prev: any) => ({ ...prev, hasBattery: false }));
    }
  }, [values.parcelOrdDoc, values.hasBattery, setValues]);

  useEffect(() => {
    if (!values.destination) {
      setErrors((prev: any) => ({ ...prev, destination: undefined }));
      return;
    }
    const country = receiverCountries.find((c) => c.iso2Country === values.destination);
    if (!country) return;
    if (values.serviceType === "A" && !country.air) {
      setErrors((prev: any) => ({
        ...prev,
        destination: "Servicio Aéreo no disponible para este destino",
      }));
    } else if (values.serviceType === "M" && !country.maritime) {
      setErrors((prev: any) => ({
        ...prev,
        destination: "Servicio Marítimo no disponible para este destino",
      }));
    } else {
      setErrors((prev: any) => ({ ...prev, destination: undefined }));
    }
  }, [values.destination, values.serviceType, receiverCountries, setErrors]);

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const data = await utilsService.getCurrencies();
        setCurrencies(data);
      } catch (err) {
        console.error("Error fetching currencies:", err);
      }
    };
    fetchCurrencies();
  }, []);

  useEffect(() => {
    const fetchReceiverDniTypes = async () => {
      if (!values.destination) return;
      try {
        const receiverData = await utilsService.getDniTypes(values.destination);
        setReceiverDniTypes(receiverData);
      } catch (error) {
        console.error("Error fetching receiver DNI types:", error);
      }
    };
    fetchReceiverDniTypes();
  }, [values.destination]);

  useEffect(() => {
    // Handle incoming state from Landing Page (PickupForm) or CalculatorPage
    const state = location.state as any;
    if (state && !isPreferenceInitialized) {
      if (state.fromLanding) {
        const firstName = state.firstName || (state.fullName || "").trim().split(/\s+/)[0] || "";
        const lastName = state.lastName || (state.fullName || "").trim().split(/\s+/).slice(1).join(" ") || "";

        setValues((prev: any) => ({
          ...prev,
          senderFirstName: firstName,
          senderLastName: lastName,
          senderPhone: state.phone || "",
          senderEmail: state.email || "",
          senderAddressLine1: state.address || "",
          senderAddressLine2: "",
          senderCity: state.city || "",
          senderState: state.state || "",
          originZip: state.zipCode || "",
          packageContent: state.packageContent || state.notes || "",
          pickupDate: state.date || prev.pickupDate,
        }));

        if (state.zipCode) {
          setZipValidatedByField((prev) => ({ ...prev, originZip: true }));
        }

        if (state.email) {
          validateEmailExistence(state.email);
        }

        // Always default to Drop Off ($0.00 USD - INCLUIDO)
        setPickupSelected(false);
        setElpaketDispatcherSelected(false);
        userWantsPickupRef.current = false;
        userWantsElpaketDispatcherRef.current = false;
        setIsPreferenceInitialized(true);

        setCurrentStep(1);
      } else if (state.fromCalculator) {
        setValues((prev: any) => ({
          ...prev,
          origin: state.origin || prev.origin,
          destination: state.destination || prev.destination,
          originZip: state.originZip || prev.originZip,
          destinationZip: state.destinationZip || prev.destinationZip,
          weight: state.weight !== undefined ? state.weight : prev.weight,
          width: state.width !== undefined ? state.width : prev.width,
          height: state.height !== undefined ? state.height : prev.height,
          length: state.length !== undefined ? state.length : prev.length,
          fob: state.fob !== undefined ? state.fob : prev.fob,
          unitSystem: state.unitSystem || prev.unitSystem,
          currency: state.currency || prev.currency,
          parcelOrdDoc: state.parcelOrdDoc || prev.parcelOrdDoc,
          serviceType: state.serviceType || prev.serviceType,
          hasBattery: state.hasBattery !== undefined ? state.hasBattery : prev.hasBattery,
          senderAddressLine1: state.senderAddressLine1 || prev.senderAddressLine1,
          senderCity: state.senderCity || prev.senderCity,
          senderState: state.senderState || prev.senderState,
          receiverAddressLine1: state.receiverAddressLine1 || prev.receiverAddressLine1,
          receiverCity: state.receiverCity || prev.receiverCity,
          receiverState: state.receiverState || prev.receiverState,
          pickupDate: state.pickupDate || prev.pickupDate,
          selectedService: state.selectedService || prev.selectedService,
          idCarrier: state.idCarrier || prev.idCarrier,
          idCarrierService: state.idCarrierService !== undefined ? state.idCarrierService : prev.idCarrierService,
        }));

        if (state.originZip) {
          setZipValidatedByField((prev) => ({ ...prev, originZip: true }));
        }
        if (state.destinationZip) {
          setZipValidatedByField((prev) => ({ ...prev, destinationZip: true }));
        }

        if (state.pickupSelected !== undefined) {
          setPickupSelected(state.pickupSelected);
          userWantsPickupRef.current = state.pickupSelected;
        }
        if (state.elpaketDispatcherSelected !== undefined) {
          setElpaketDispatcherSelected(state.elpaketDispatcherSelected);
          userWantsElpaketDispatcherRef.current = state.elpaketDispatcherSelected;
        }
        if (state.selectedExternalOption !== undefined) {
          setSelectedExternalOption(state.selectedExternalOption);
        }
        setIsPreferenceInitialized(true);

        // Keep it on step 1 to review package details and add packageContent description
        setCurrentStep(1);
      }
    }
  }, [location.state, setValues, setPickupSelected, setElpaketDispatcherSelected, setSelectedExternalOption, setIsPreferenceInitialized]);

  const fetchPaymentIntent = async (amount: number, currency: string) => {
    setLoadingPayment(true);
    try {
      const description = `Servicio de Pickup - ${values.senderFirstName} ${values.senderLastName}`;
      const response = await paymentService.createPaymentIntent(
        amount,
        currency,
        description,
        values.senderEmail,
      );
      setClientSecret(response.clientSecret);
      setPaymentIntentId(response.id);
      return true;
    } catch (error) {
      console.error("Error fetching payment intent:", error);
      errorService.showErrorAlert(error, "Error de Pago");
      return false;
    } finally {
      setLoadingPayment(false);
    }
  };

  const executeShipmentCreation = async (
    paymentMethodId?: string,
    confirmedIntentId?: string,
  ) => {
    setIsProcessingOrder(true);

    const isNational = values.origin === values.destination;

    const payload: CreateOrderRequest = {
      idCarrier: values.idCarrier || 1,
      idCarrierService: values.idCarrierService,
      idExternalCourier: isNational ? null : (elpaketDispatcherSelected ? null : (externalServices[0]?.idExternalCourier || null)),
      idExternalCourierService: isNational ? null : (elpaketDispatcherSelected ? null : (selectedExternalOption?.idExternalCourierService || null)),
      idExternalAdditionalService: elpaketDispatcherSelected
        ? (externalServices[0]?.elpaketDispatcherService ? externalServices[0].elpaketDispatcherService.idExternalCourierService : null)
        : ((pickupSelected && externalServices[0]?.pickUpService) ? externalServices[0].pickUpService.idExternalCourierService : null),
      senderDetails: {
        firstName: values.senderFirstName,
        lastName: values.senderLastName,
        email: values.senderEmail,
        PhoneNumber: selectedSenderCountry?.phoneCode
          ? `${selectedSenderCountry.phoneCode}${values.senderPhone}`
          : `+1${values.senderPhone}`,
        KindOfDNI: findDniTypeByCompositeKey(senderDniTypes, values.senderKindOfDNI)?.kindOfDNI || values.senderKindOfDNI,
        DNI: values.senderDNI,
        AddressLine1: values.senderAddressLine1,
        AddressLine2: values.senderAddressLine2 || "",
        City: values.senderCity,
        State: values.senderState,
        Municipality: values.municipalityOrigin || values.senderCity,
        ZipCode: values.originZip,
        iso2Country: values.origin,
        isThirdPerson: !!values.isThirdPerson,
      },
      receiverDetails: {
        firstName: values.receiverFirstName,
        lastName: values.receiverLastName,
        email: values.receiverEmail,
        PhoneNumber: selectedReceiverCountry?.phoneCode
          ? `${selectedReceiverCountry.phoneCode}${values.receiverPhone}`
          : values.receiverPhone,
        KindOfDNI: findDniTypeByCompositeKey(receiverDniTypes, values.receiverKindOfDNI)?.kindOfDNI || values.receiverKindOfDNI,
        DNI: values.receiverDNI,
        AddressLine1: values.receiverAddressLine1,
        AddressLine2: values.receiverAddressLine2 || "",
        City: values.receiverCity,
        State: values.receiverState,
        Municipality: values.municipalityDestiny || values.receiverCity,
        ZipCode: values.destinationZip,
        iso2Country: values.destination,
        isThirdPerson: false,
      },
      parcelData: {
        serviceType: values.serviceType,
        height: Number(values.height),
        width: Number(values.width),
        length: Number(values.length),
        weight: Number(values.weight),
        parcelOrdDoc: values.parcelOrdDoc,
        currency: values.currency,
        unitSystem: values.unitSystem.toUpperCase(),
        fob: Number(values.fob),
        content: values.packageContent,
      },
      paymentMethodId: paymentMethodId,
      pickupDate: values.pickupDate || undefined,
      hasBattery: values.hasBattery,
      DocumentoBase64: selectedDocument ? selectedDocument.base64string : undefined,
      DocumentoExt: selectedDocument ? selectedDocument.ext : undefined,
    };

    try {
      const response = await orderService.createOrder(payload);
      if (response.statusCode === 200) {
        setOrderSuccessData(response.data);
        navigate(
          "/customer/send-package?orderNumber=" + response.data.orderNumber,
        );
      } else if (response.statusCode === 202 && response.clientSecret) {
        setAllowRedirect(true);
        const stripe = await stripePromise;
        if (!stripe) throw new Error("Stripe context not found");

        const { error, paymentIntent } = await stripe.handleNextAction({
          clientSecret: response.clientSecret,
        });

        if (error) {
          Swal.fire({
            icon: "error",
            title: "Error de Verificación",
            text: error.message || "La verificación del pago falló.",
          });
        } else if (paymentIntent && paymentIntent.status === "succeeded") {
          await executeShipmentCreation(paymentMethodId, paymentIntent.id);
        } else {
          Swal.fire({
            icon: "warning",
            title: "Acción Pendiente",
            text: "El pago aún requiere atención.",
          });
        }
      } else {
        errorService.showErrorAlert(response, "Error");
      }
    } catch (error) {
      console.error("Error in executeShipmentCreation:", error);
      errorService.showErrorAlert(error, "Error");
    } finally {
      setIsProcessingOrder(false);
    }
  };

  const getPayloadKey = (sourceValues: any) => JSON.stringify({
    origin: sourceValues.origin,
    destination: sourceValues.destination,
    height: Number(sourceValues.height),
    width: Number(sourceValues.width),
    length: Number(sourceValues.length),
    weight: Number(sourceValues.weight),
    parcelOrdDoc: sourceValues.parcelOrdDoc,
    currency: sourceValues.currency,
    unitSystem: sourceValues.unitSystem,
    fob: Number(sourceValues.fob),
    originZip: sourceValues.originZip,
    destinationZip: sourceValues.destinationZip,
    pickupDate: sourceValues.pickupDate,
  });

  const handleCalculate = async (overrideValues?: any) => {
    const sourceValues = overrideValues || values;
    lastCalculatedPayloadRef.current = getPayloadKey(sourceValues);
    const payload: RatePayload = {
      iso2Origin: sourceValues.origin,
      iso2Destination: sourceValues.destination,
      serviceType: sourceValues.serviceType,
      height: Number(sourceValues.height),
      width: Number(sourceValues.width),
      length: Number(sourceValues.length),
      weight: Number(sourceValues.weight),
      parcelOrdDoc: sourceValues.parcelOrdDoc,
      currency: sourceValues.currency,
      unitSystem: sourceValues.unitSystem === "metric" ? "METRIC" : "IMPERIAL",
      fob: Number(sourceValues.fob),
      zipcodeOrigin: sourceValues.originZip,
      zipcodeDestination: sourceValues.destinationZip,
      addressLineOrigin: sourceValues.senderAddressLine1,
      cityOrigin: sourceValues.senderCity,
      stateOrigin: sourceValues.senderState,
      stateDestiny: sourceValues.receiverState,
      municipalityDestiny: sourceValues.municipalityDestiny,
      pickupDate: sourceValues.pickupDate,
      externalCarrierName: "UPS",
    };

    try {
      setIsCalculating(true);
      const results = await rateService.calculateRate(payload);
      const sortedResults = rateService.sortByCheapest(results);
      setRateResults(sortedResults);

      if (sourceValues.origin === sourceValues.destination && sortedResults.length > 0) {
        const nationalRate = sortedResults[0];
        const sortedServices = [...(nationalRate.services || [])].sort((a, b) => a.serviceSubTotal - b.serviceSubTotal);
        const stillAvailableService = sortedServices.find(s => s.idCarrierService === sourceValues.idCarrierService);
        const selectedCarrierService = stillAvailableService ? stillAvailableService.idCarrierService : (sortedServices[0]?.idCarrierService || null);
        setValues((prev: any) => ({
          ...prev,
          selectedService: nationalRate.carrierName,
          idCarrier: nationalRate.idCarrier || 1,
          idCarrierService: selectedCarrierService,
        }));
        if (nationalRate.externalServices && nationalRate.externalServices.length > 0) {
          setExternalServices(nationalRate.externalServices as any);
        }
        const hasPickupService = !!(nationalRate.externalServices?.[0]?.pickUpService);
        const hasDispatcherService = !!(nationalRate.externalServices?.[0]?.elpaketDispatcherService);

        if (userWantsElpaketDispatcherRef.current && hasDispatcherService) {
          setElpaketDispatcherSelected(true);
          setPickupSelected(false);
        } else if (userWantsPickupRef.current && hasPickupService) {
          setPickupSelected(true);
          setElpaketDispatcherSelected(false);
        } else {
          setPickupSelected(false);
          setElpaketDispatcherSelected(false);
        }
      } else if (sortedResults.length > 0) {
        const stillAvailable = sortedResults.find(
          (r) => r.carrierName === sourceValues.selectedService &&
                 (sourceValues.idCarrierService ? r.idCarrierService === sourceValues.idCarrierService : true)
        );
        const autoSelected = stillAvailable || sortedResults[0];
        const updatedVals = {
          ...sourceValues,
          selectedService: autoSelected.carrierName,
          idCarrier: autoSelected.idCarrier || sourceValues.idCarrier,
          idCarrierService: autoSelected.idCarrierService !== undefined ? autoSelected.idCarrierService : null,
        };
        setValues((prev: any) => ({
          ...prev,
          ...updatedVals,
        }));
        await fetchExternalRatesHelper(updatedVals);
      } else {
        setValues((prev: any) => ({ ...prev, selectedService: "" }));
      }
      return true;
    } catch (error) {
      console.error("Error calculating rates:", error);
      setRateResults([]);
      errorService.showErrorAlert(error, "Error");
      return false;
    } finally {
      setIsCalculating(false);
    }
  };

  useEffect(() => {
    if (currentStep === 4) {
      handleCalculate();
    }
  }, [currentStep]);
  const handleSavePackageData = async (updatedData: any) => {
    const prevService = values.selectedService;
    const updatedValues = { ...values, ...updatedData };
    setValues(updatedValues);
    if (updatedData.selectedDocument !== undefined) {
      setSelectedDocument(updatedData.selectedDocument);
    }

    const success = await handleCalculate(updatedValues);

    if (success) {
      setRateResults((prevResults) => {
        const stillAvailable = prevResults.find(
          (r) => r.carrierName === prevService,
        );
        if (stillAvailable) {
          setValues((v) => ({ ...v, selectedService: prevService }));
        }
        return prevResults;
      });

      Swal.fire({
        icon: "success",
        title: "Información Actualizada",
        text: "Las tarifas han sido recalculadas.",
        timer: 1500,
        showConfirmButton: false,
        toast: true,
        position: "top-end",
      });
    }
  };

  const handleSaveSenderData = async (updatedData: any) => {
    const updatedValues = {
      ...values,
      senderFirstName: updatedData.firstName !== undefined ? updatedData.firstName : values.senderFirstName,
      senderLastName: updatedData.lastName !== undefined ? updatedData.lastName : values.senderLastName,
      senderEmail: updatedData.email !== undefined ? updatedData.email : values.senderEmail,
      senderPhone: updatedData.phone !== undefined ? updatedData.phone : values.senderPhone,
      senderKindOfDNI: updatedData.kindOfDNI !== undefined ? updatedData.kindOfDNI : values.senderKindOfDNI,
      senderDNI: updatedData.dni !== undefined ? updatedData.dni : values.senderDNI,
      senderAddressLine1: updatedData.addressLine1,
      senderAddressLine2: updatedData.addressLine2,
      senderCity: updatedData.city,
      senderState: updatedData.state,
      originZip: updatedData.zipCode,
    };
    setValues(updatedValues);
    setErrors((prev: any) => ({
      ...prev,
      senderFirstName: undefined,
      senderLastName: undefined,
      senderEmail: undefined,
      senderPhone: undefined,
      senderKindOfDNI: undefined,
      senderDNI: undefined,
      senderAddressLine1: undefined,
      senderCity: undefined,
      senderState: undefined,
      originZip: undefined,
    }));
    setZipValidatedByField((prev) => ({
      ...prev,
      originZip: true,
    }));

    const prevService = values.selectedService;
    const success = await handleCalculate(updatedValues);

    if (success) {
      setRateResults((prevResults) => {
        const stillAvailable = prevResults.find(
          (r) => r.carrierName === prevService,
        );
        if (stillAvailable) {
          setValues((v) => ({ ...v, selectedService: prevService }));
        }
        return prevResults;
      });

      Swal.fire({
        icon: "success",
        title: "Información Actualizada",
        text: "Las tarifas han sido recalculadas.",
        timer: 1500,
        showConfirmButton: false,
        toast: true,
        position: "top-end",
      });
    }
  };

  const handleSaveReceiverData = async (updatedData: any) => {
    const updatedValues = {
      ...values,
      receiverFirstName: updatedData.firstName || values.receiverFirstName,
      receiverLastName: updatedData.lastName || values.receiverLastName,
      receiverEmail: updatedData.email || values.receiverEmail,
      receiverPhone: updatedData.phone || values.receiverPhone,
      receiverKindOfDNI: updatedData.kindOfDNI || values.receiverKindOfDNI,
      receiverDNI: updatedData.dni || values.receiverDNI,
      receiverAddressLine1: updatedData.addressLine1,
      receiverAddressLine2: updatedData.addressLine2,
      receiverCity: updatedData.city,
      receiverState: updatedData.state,
      destinationZip: updatedData.zipCode,
      municipalityDestiny: updatedData.city,
      stateDestiny: updatedData.state,
    };
    setValues(updatedValues);
    setErrors((prev: any) => ({
      ...prev,
      receiverAddressLine1: undefined,
      receiverCity: undefined,
      receiverState: undefined,
      destinationZip: undefined,
      receiverKindOfDNI: undefined,
      receiverDNI: undefined,
    }));
    setZipValidatedByField((prev) => ({
      ...prev,
      destinationZip: true,
    }));

    const prevService = values.selectedService;
    const success = await handleCalculate(updatedValues);

    if (success) {
      setRateResults((prevResults) => {
        const stillAvailable = prevResults.find(
          (r) => r.carrierName === prevService,
        );
        if (stillAvailable) {
          setValues((v) => ({ ...v, selectedService: prevService }));
        }
        return prevResults;
      });

      Swal.fire({
        icon: "success",
        title: "Información Actualizada",
        text: "Las tarifas han sido recalculadas.",
        timer: 1500,
        showConfirmButton: false,
        toast: true,
        position: "top-end",
      });
    }
  };


  const validateEmailExistence = async (email: string) => {
    if (!email) {
      setEmailExists(null);
      setExistingCustomerName("");
      return;
    }
    setIsValidatingEmail(true);
    try {
      const result = await authService.checkCustomerExist(email);
      console.log("CheckCustomerExist result:", result);
      setEmailExists(result.exist);
      if (result.exist && result.data) {
        const firstName = result.data.FirstName || (result.data as any).firstName || "";
        const lastName = result.data.LastName || (result.data as any).lastName || "";
        setExistingCustomerName(`${firstName} ${lastName}`.trim());
      } else {
        setExistingCustomerName("");
      }
    } catch (error) {
      console.error("Error checking email existence:", error);
      setEmailExists(null);
      setExistingCustomerName("");
    } finally {
      setIsValidatingEmail(false);
    }
  };

  const getEstimatedArrivalText = (rate: RateOption) => {
    const isThisSelected = values.selectedService === rate.carrierName && values.idCarrierService === rate.idCarrierService;
    const activeExternalOption = isThisSelected ? selectedExternalOption : null;

    if (!activeExternalOption) {
      if (rate.estimatedMinDays && rate.estimatedMaxDays) {
        return `Entrega estimada: ${rate.estimatedMinDays} - ${rate.estimatedMaxDays} días`;
      }
      return "";
    }

    if (!activeExternalOption.transitDays || activeExternalOption.transitDays === "N/A") {
      return "El tiempo de entrega final puede ajustarse a la jornada de los couriers.";
    }

    const extDays = parseInt(activeExternalOption.transitDays) || 0;
    const minDays = rate.estimatedMinDays || 0;
    const maxDays = rate.estimatedMaxDays || 0;

    // Si se seleccionó pickup, se usa values.pickupDate como inicio y se suma 1 día de recogida.
    // De lo contrario (Drop off), se empieza desde hoy y no se suma día de recogida.
    const start = pickupSelected ? values.pickupDate : new Date().toISOString().split('T')[0];
    const pickupExtraDay = pickupSelected ? 1 : 0;

    const addBusinessDays = (startDateStr: string, daysToAdd: number): Date => {
      const date = new Date(startDateStr + 'T12:00:00');
      let added = 0;
      while (added < daysToAdd) {
        date.setDate(date.getDate() + 1);
        const day = date.getDay();
        if (day !== 0 && day !== 6) {
          added++;
        }
      }
      return date;
    };

    const formatDateSpanish = (d: Date): string => {
      const months = ['ene.', 'feb.', 'mar.', 'abr.', 'may.', 'jun.', 'jul.', 'ago.', 'sep.', 'oct.', 'nov.', 'dic.'];
      return `${d.getDate()} de ${months[d.getMonth()]}`;
    };

    try {
      const minDate = addBusinessDays(start, extDays + minDays + pickupExtraDay);
      const maxDate = addBusinessDays(start, extDays + maxDays + pickupExtraDay);
      return `Entrega estimada: ${formatDateSpanish(minDate)} - ${formatDateSpanish(maxDate)}`;
    } catch {
      return "El tiempo de entrega final puede ajustarse a la jornada de los couriers.";
    }
  };

  const getServiceKey = (rate: RateOption) => {
    return `${rate.carrierName}-${rate.idCarrierService || 0}`;
  };

  const selectExternalService = (rate: RateOption, newIndices: Set<number>) => {
    const key = getServiceKey(rate);
    setSelectedExternalIndicesByCarrier((prev) => {
      const newMap = new Map(prev);
      newMap.set(key, newIndices);
      return newMap;
    });
  };

  const currentSelectedRate = React.useMemo(() => {
    // National rates group the selectable UPS services (Ground, 2nd Day, etc.)
    // inside a single carrier rate. In that mode idCarrierService belongs to the
    // selected nested service, so it must not be matched against the wrapper rate.
    if (values.origin === values.destination) {
      return rateResults.find(
        (r) => r.carrierName === values.selectedService,
      );
    }

    return rateResults.find(
      (r) =>
        r.carrierName === values.selectedService &&
        r.idCarrierService === values.idCarrierService,
    );
  }, [values.origin, values.destination, values.selectedService, values.idCarrierService, rateResults]);

  const fetchExternalRatesHelper = async (sourceValues: any) => {
    if (sourceValues.origin === sourceValues.destination) {
      if (rateResults[0]?.externalServices && rateResults[0].externalServices.length > 0) {
        setExternalServices(rateResults[0].externalServices as any);
      }
      setLoadingExternal(false);
      return;
    }
    if (!sourceValues.selectedService || !sourceValues.idCarrier) {
      setExternalServices([]);
      setSelectedExternalOption(null);
      setPickupSelected(false);
      setElpaketDispatcherSelected(false);
      return;
    }
    setLoadingExternal(true);
    try {
      const payload: CalculateExternalRatePayload = {
        idCarrier: sourceValues.idCarrier,
        iso2Origin: sourceValues.origin,
        iso2Destination: sourceValues.destination,
        height: Number(sourceValues.height),
        width: Number(sourceValues.width),
        length: Number(sourceValues.length),
        weight: Number(sourceValues.weight),
        unitSystem: sourceValues.unitSystem === 'metric' ? 'METRIC' : 'IMPERIAL',
        zipcodeOrigin: sourceValues.originZip,
        addressLineOrigin: sourceValues.senderAddressLine1,
        cityOrigin: sourceValues.senderCity,
        stateOrigin: sourceValues.senderState,
        pickupDate: sourceValues.pickupDate,
      };
      const results = await rateService.calculateExternalRate(payload);
      setExternalServices(results);
      
      let matchedOption: ExternalRateServiceOption | null = null;
      if (selectedExternalOption) {
        const allNewOptions = results.flatMap(r => r.options);
        matchedOption = allNewOptions.find(o => o.serviceName === selectedExternalOption.serviceName) || null;
      }

      if (matchedOption) {
        setSelectedExternalOption(matchedOption);
      } else if (results.length > 0 && results[0].options.length > 0) {
        setSelectedExternalOption(results[0].options[0]);
      } else {
        setSelectedExternalOption(null);
      }

      if (results.length > 0) {
        const hasPickupService = !!results[0].pickUpService;
        const hasDispatcherService = !!results[0].elpaketDispatcherService;

        if (userWantsElpaketDispatcherRef.current && hasDispatcherService) {
          setElpaketDispatcherSelected(true);
          setPickupSelected(false);
        } else if (userWantsPickupRef.current && hasPickupService) {
          setPickupSelected(true);
          setElpaketDispatcherSelected(false);
        } else {
          setPickupSelected(false);
          setElpaketDispatcherSelected(false);
        }
      }
    } catch (error) {
      console.error('Error fetching external rates:', error);
      setExternalServices([]);
      setSelectedExternalOption(null);
    } finally {
      setLoadingExternal(false);
    }
  };

  const dynamicTotal = React.useMemo(() => {
    const isNational = values.origin === values.destination;

    if (isNational) {
      const nationalRate = rateResults[0];
      if (!nationalRate) { console.log('[TOTAL] isNational=true but no nationalRate'); return 0; }

      // Carrier service price: find the selected UPS sub-service
      const services = nationalRate.services || [];
      const selectedServiceEntry = services.find(s => s.idCarrierService === values.idCarrierService);
      const carrierServicePrice = selectedServiceEntry
        ? selectedServiceEntry.serviceSubTotal
        : (services[0]?.serviceSubTotal ?? 0);

      // Additional: elPaket dispatcher OR pickup
      const elpaketSvc = (externalServices[0] as any)?.elpaketDispatcherService;
      const pickupSvc = (externalServices[0] as any)?.pickUpService;
      const elpaketPrice = (elpaketDispatcherSelected && elpaketSvc) ? (elpaketSvc.serviceSubTotal ?? 0) : 0;
      const pickupPrice = (pickupSelected && !elpaketDispatcherSelected && pickupSvc) ? (pickupSvc.serviceSubTotal ?? 0) : 0;

      return carrierServicePrice + elpaketPrice + pickupPrice;
    }

    // ── International mode ──
    if (!currentSelectedRate) return 0;
    const carrierBaseRate = currentSelectedRate.rate || 0;
    const externalPrice = (selectedExternalOption && !elpaketDispatcherSelected) ? selectedExternalOption.serviceSubTotal : 0;
    const elpaketPrice = (elpaketDispatcherSelected && externalServices[0]?.elpaketDispatcherService) ? externalServices[0].elpaketDispatcherService.serviceSubTotal : 0;
    const pickupPrice = (pickupSelected && !elpaketDispatcherSelected && externalServices[0]?.pickUpService) ? externalServices[0].pickUpService.serviceSubTotal : 0;
    return carrierBaseRate + externalPrice + pickupPrice + elpaketPrice;
  }, [currentSelectedRate, rateResults, selectedExternalOption, pickupSelected, elpaketDispatcherSelected, externalServices, values.origin, values.destination, values.idCarrierService]);

  useEffect(() => {
    if (rateResults.length > 0) {
      setSelectedExternalIndicesByCarrier((prev) => {
        const newMap = new Map<string, Set<number>>();
        rateResults.forEach((rate) => {
          const key = getServiceKey(rate);
          if (prev.has(key)) {
            newMap.set(key, prev.get(key)!);
          } else {
            newMap.set(key, rateService.getInitialExternalIndices(rate));
          }
        });
        return newMap;
      });
    }
  }, [rateResults]);

  const isStep1Valid = React.useMemo(() => {
    const required = [
      "weight",
      "width",
      "height",
      "length",
      "fob",
      "packageContent",
    ];
    const baseValid = required.every(
      (field) =>
        values[field as keyof typeof values] &&
        !errors[field as keyof typeof errors],
    ) && disclaimerChecked;

    if (Number(values.fob) >= 250) {
      return baseValid && selectedDocument !== null;
    }
    return baseValid;
  }, [values, errors, disclaimerChecked, selectedDocument]);

  const isSenderStepValid = React.useMemo(() => {
    const selectedDniObj = findDniTypeByCompositeKey(senderDniTypes, values.senderKindOfDNI);
    const requiresRazon = selectedDniObj?.requiereRazonSocial === true;
    const required = [
      "senderFirstName",
      "senderLastName",
      "senderEmail",
      "senderPhone",
      "senderKindOfDNI",
      "senderDNI",
      "senderAddressLine1",
      "senderCity",
      "senderState",
      "originZip",
    ];
    if (requiresRazon) {
      required.push("senderRazonSocial");
    }
    if (values.isThirdPerson) {
      required.push("userFirstName", "userLastName", "userEmail", "userCountry");
    }
    return (
      required.every(
        (field) =>
          values[field as keyof typeof values] &&
          !errors[field as keyof typeof errors],
      ) &&
      zipValidatedByField.originZip
    );
  }, [values, errors, zipValidatedByField.originZip]);

  const isServiceTypeAvailable = React.useMemo(() => {
    if (!values.destination) return true;
    const country = receiverCountries.find((c) => c.iso2Country === values.destination);
    if (!country) return true;
    if (values.serviceType === "A") return !!country.air;
    if (values.serviceType === "M") return !!country.maritime;
    return true;
  }, [values.destination, values.serviceType, receiverCountries]);

  const isReceiverStepValid = React.useMemo(() => {
    const required = [
      "receiverFirstName",
      "receiverLastName",
      "receiverEmail",
      "receiverPhone",
      "receiverKindOfDNI",
      "receiverDNI",
      "receiverAddressLine1",
      "receiverCity",
      "receiverState",
      "destinationZip",
    ];
    return (
      required.every(
        (field) =>
          values[field as keyof typeof values] &&
          !errors[field as keyof typeof errors],
      ) &&
      zipValidatedByField.destinationZip &&
      isServiceTypeAvailable
    );
  }, [values, errors, zipValidatedByField.destinationZip, isServiceTypeAvailable]);

  const validateStep = (step: number): boolean => {
    let stepFields: string[] = [];
    if (step === 1) {
      stepFields = ["length", "width", "height", "weight", "fob", "packageContent"];
    } else if (step === 2) {
      const selectedDniObj = findDniTypeByCompositeKey(senderDniTypes, values.senderKindOfDNI);
      const requiresRazon = selectedDniObj?.requiereRazonSocial === true;
      stepFields = [
        "senderFirstName",
        "senderLastName",
        "senderEmail",
        "senderPhone",
        "senderKindOfDNI",
        "senderDNI",
        "senderAddressLine1",
        "senderCity",
        "senderState",
        "originZip",
      ];
      if (requiresRazon) {
        stepFields.push("senderRazonSocial");
      }
      if (values.isThirdPerson) {
        stepFields.push("userFirstName", "userLastName", "userEmail", "userCountry");
      }
    } else if (step === 3) {
      stepFields = [
        "receiverFirstName",
        "receiverLastName",
        "receiverEmail",
        "receiverPhone",
        "receiverKindOfDNI",
        "receiverDNI",
        "receiverAddressLine1",
        "receiverCity",
        "receiverState",
        "destinationZip",
      ];
    }

    let isStepValid = true;
    const newErrors = { ...errors } as any;
    let firstFailedField: string | null = null;

    stepFields.forEach((field) => {
      const rule = (validationRules as any)[field];
      if (rule) {
        const error = rule((values as any)[field], values);
        if (error) {
          newErrors[field] = error;
          isStepValid = false;
          if (!firstFailedField) {
            firstFailedField = field;
          }
        } else {
          newErrors[field] = null;
        }
      }
    });

    if (step === 1) {
      if (!disclaimerChecked) {
        isStepValid = false;
        setDisclaimerError(true);
      } else {
        setDisclaimerError(false);
      }


    }

    if (step === 2 && isStepValid && !zipValidatedByField.originZip) {
      isStepValid = false;
      newErrors.originZip = "Debe validar el código postal";
      firstFailedField = "originZip";
    }

    if (step === 3 && isStepValid && !zipValidatedByField.destinationZip) {
      isStepValid = false;
      newErrors.destinationZip = "Debe validar el código postal";
      firstFailedField = "destinationZip";
    }

    setErrors(newErrors);

    if (!isStepValid) {
      if (firstFailedField) {
        let element = document.getElementsByName(firstFailedField)[0] as HTMLElement;
        if (!element) {
          element = document.getElementById(firstFailedField) as HTMLElement;
        }
        if (element) {
          element.focus();
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      } else if (step === 1 && !disclaimerChecked) {
        const element = document.getElementById("disclaimer-checkbox-container");
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    }

    return isStepValid;
  };

  const confirmDeliveryMethod = async (): Promise<boolean> => {
    const pickupSvc = externalServices[0]?.pickUpService;
    const elpaketSvc = externalServices[0]?.elpaketDispatcherService;
    const altSvc = elpaketSvc || pickupSvc;
    if (!altSvc) return true;
    const currency = currentSelectedRate?.currency || "USD";
    const price = `$${altSvc.serviceSubTotal.toFixed(2)} ${currency}`;
    const pickupChosen = pickupSelected || elpaketDispatcherSelected;
    const pickupAgent = (elpaketDispatcherSelected || (!pickupChosen && elpaketSvc))
      ? "nuestro transportista ElPaket"
      : "un chofer oficial UPS";

    let formattedPickupDate = "";
    if (pickupChosen && values.pickupDate) {
      try {
        const [y, m, d] = values.pickupDate.split("-");
        const pickupDateObj = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10), 12, 0, 0);
        const days = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
        const months = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
        formattedPickupDate = `${days[pickupDateObj.getDay()]}, ${pickupDateObj.getDate()} de ${months[pickupDateObj.getMonth()]} de ${pickupDateObj.getFullYear()}`;
      } catch (e) {
        formattedPickupDate = values.pickupDate;
      }
    }

    const result = await Swal.fire({
      title: "Confirme su opción de envío",
      html: pickupChosen
        ? `Ha seleccionado el servicio de <b>Pickup</b>.<br/>${pickupAgent === "nuestro transportista ElPaket" ? "Nuestro transportista ElPaket" : "Un conductor autorizado de UPS"} recogerá su paquete en su casa u oficina${formattedPickupDate ? ` el <b>${formattedPickupDate}</b>` : ""}${elpaketDispatcherSelected ? "" : " entre las 8:00 a. m. y las 05:00 p. m."}.<br/><br/>También puede llevar su paquete a la tienda UPS de su preferencia (<b>Drop Off</b>) y <b>ahorrar ${price}</b>.`
        : `Ha seleccionado la modalidad <b>Drop Off</b>.<br/>Puede llevar su paquete a la tienda UPS más cercana de su preferencia <b>sin costo adicional</b>.<br/><br/>También puede solicitar el servicio de <b>Pickup</b> para que ${pickupAgent === "nuestro transportista ElPaket" ? "nuestro transportista ElPaket" : "un conductor autorizado de UPS"} recoja su paquete en la comodidad de su casa u oficina.<br/>Cambie su opción a Pickup para consultar la tarifa disponible para su envío.`,
      icon: "info",
      showCancelButton: true,
      confirmButtonText: "Confirmar",
      cancelButtonText: "Cambiar opción",
    });
    return result.isConfirmed;
  };

  const handleNextStep = async () => {
    if (currentStep === 1) {
      if (validateStep(1)) {
        setCurrentStep(2);
      }
    } else if (currentStep === 2) {
      if (validateStep(2)) {
        setCurrentStep(3);
      }
    } else if (currentStep === 3) {
      if (validateStep(3)) {
        const currentKey = getPayloadKey(values);
        if (rateResults.length === 0 || lastCalculatedPayloadRef.current !== currentKey) {
          const success = await handleCalculate();
          if (success) setCurrentStep(4);
        } else {
          setCurrentStep(4);
        }
      }
    } else if (currentStep === 4 && values.selectedService) {
      const confirmed = await confirmDeliveryMethod();
      if (!confirmed) return;
      const hasActiveSession = !!tokenService.getCustomerDataToken();
      if (hasActiveSession || isAuthForPickUp) {
        setIsAuthForPickUp(true);
        setCurrentStep(5);
        return;
      }
      setShowAuthModal(true);
    } else if (currentStep === 5) {
      const success = await fetchPaymentIntent(
        dynamicTotal,
        currentSelectedRate?.currency || "USD",
      );
      if (success) setCurrentStep(6);
    }
  };

  const performAuth = async (password: string): Promise<boolean> => {
    try {
      setIsCalculating(true);
      const emailToAuth = values.isThirdPerson ? values.userEmail : values.senderEmail;
      const firstNameToAuth = values.isThirdPerson ? values.userFirstName : values.senderFirstName;
      const lastNameToAuth = values.isThirdPerson ? values.userLastName : values.senderLastName;
      const phoneToAuth = values.isThirdPerson ? "" : `+1${values.senderPhone}`;
      const dniToAuth = values.isThirdPerson ? "" : values.senderDNI;
      const kindOfDniToAuth = values.isThirdPerson
        ? ""
        : (findDniTypeByCompositeKey(senderDniTypes, values.senderKindOfDNI)?.kindOfDNI || values.senderKindOfDNI);
      const razonSocialToAuth = values.isThirdPerson ? "" : values.senderRazonSocial;

      const loginRes = await authService.loginWithEmail(
        emailToAuth,
        password,
      );

      if (loginRes.success) {
        syncToken();
        const customerData = loginRes.customerData;
        if (customerData) {
          if (!values.isThirdPerson) {
            setValues((prev: any) => ({
              ...prev,
              senderPhone: prev.senderPhone || customerData.PhoneNumber,
              senderDNI: prev.senderDNI ? prev.senderDNI : (customerData.DNI || prev.senderDNI),
              senderKindOfDNI: prev.senderDNI ? prev.senderKindOfDNI : (customerData.KindOfDNI || prev.senderKindOfDNI),
            }));
          } else {
            setValues((prev: any) => ({
              ...prev,
              userFirstName: customerData.FirstName || prev.userFirstName,
              userLastName: customerData.LastName || prev.userLastName,
            }));
          }
        }
        setIsAuthForPickUp(true);
        setShowAuthModal(false);
        setCurrentStep(5);
        return true;
      } else if (loginRes.statusCode === 401) {
        await Swal.fire({
          icon: "error",
          title: "Contraseña incorrecta",
          text: "La contraseña ingresada no es válida. Por favor, intenta de nuevo.",
          confirmButtonColor: "#003893",
        });
        setShowAuthModal(true);
        return false;
      } else if (loginRes.statusCode === 400) {
        const regRes = await authService.registerPickupWithEmail(
          emailToAuth,
          firstNameToAuth,
          lastNameToAuth,
          phoneToAuth,
          dniToAuth,
          kindOfDniToAuth,
          password,
          values.isThirdPerson ? values.userCountry : values.origin,
          razonSocialToAuth,
        );

        if (regRes.statusCode === 200 || regRes.success) {
          syncToken();
          setIsAuthForPickUp(true);
          setShowAuthModal(false);
          setCurrentStep(5);
          return true;
        } else {
          errorService.showErrorAlert(regRes, "Error de Registro");
          return false;
        }
      } else {
        errorService.showErrorAlert(loginRes, "Error");
        return false;
      }
    } catch (error) {
      console.error("Error en auth flow:", error);
      return false;
    } finally {
      setIsCalculating(false);
    }
  };

  const handleBackStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  return (
    <div className={styles.pickupPage}>
      <SEO
        title="Servicio de Recogida UPS (Pick Up) | elPaket"
        description="Programa la recogida de paquetes a domicilio en cualquier estado de USA o gestiona la entrega en tiendas UPS para tus envíos a Latinoamérica."
        canonicalUrl="https://elpaket.com/pickup"
      />
      <Nav />

      <main className={styles.mainContent}>
        {/* Step Indicator */}
        <div className={styles.indicatorCard}>
          <div className={styles.stepIndicator}>
            {[
              { step: 1, label: "Paquete", icon: faBox },
              { step: 2, label: "Remitente", icon: faMapMarkerAlt },
              { step: 3, label: "Destinatario", icon: faUser },
              { step: 4, label: "Servicio", icon: faTruck },
              { step: 5, label: "Resumen", icon: faCalculator },
              { step: 6, label: "Pago", icon: faCreditCard },
            ].map((s) => (
              <div
                key={s.step}
                className={`${styles.step} ${currentStep === s.step ? styles.active : ""} ${currentStep > s.step ? styles.completed : ""}`}
              >
                <div className={styles.stepNumber}>
                  {currentStep > s.step ? (
                    <FontAwesomeIcon icon={faCheck} />
                  ) : (
                    <FontAwesomeIcon icon={s.icon} />
                  )}
                </div>
                <span className={styles.stepLabel}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.sendPackageMain}>
          <div className={styles.sendPackageContent}>
            {/* Step 2: Sender Details */}
            {currentStep === 2 && (
              <div className="fade-in">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', flexWrap: 'wrap', gap: '15px' }}>
                  <h3 className={styles.stepHeader} style={{ marginBottom: 0 }}>Datos del Remitente</h3>

                  {/* Sender Type Toggle */}
                  <div className={styles.unitToggle} style={{ margin: 0 }}>
                    <button
                      type="button"
                      className={`${styles.toggleBtn} ${!values.isThirdPerson ? styles.active : ""}`}
                      onClick={() => {
                        const currentCustomer = tokenService.getCustomerDataToken()?.customerData;
                        setValues((prev: any) => {
                          let matchedDni = prev.senderKindOfDNI;
                          if (currentCustomer?.KindOfDNI && senderDniTypes.length > 0) {
                            const matched = findDniTypeByCompositeKey(senderDniTypes, currentCustomer.KindOfDNI) ||
                              senderDniTypes.find(t => t.kindOfDNI.toLowerCase() === currentCustomer.KindOfDNI.toLowerCase());
                            if (matched) matchedDni = getDniCompositeKey(matched);
                          }
                          return {
                            ...prev,
                            isThirdPerson: false,
                            senderFirstName: currentCustomer?.FirstName || prev.senderFirstName,
                            senderLastName: currentCustomer?.LastName || prev.senderLastName,
                            senderEmail: currentCustomer?.Email || prev.senderEmail,
                            senderPhone: currentCustomer?.PhoneNumber ? currentCustomer.PhoneNumber.replace(/^\+1/, "") : prev.senderPhone,
                            senderKindOfDNI: matchedDni,
                            senderDNI: currentCustomer?.DNI || prev.senderDNI,
                          };
                        });
                      }}
                    >
                      Envío Propio
                    </button>
                    <button
                      type="button"
                      className={`${styles.toggleBtn} ${values.isThirdPerson ? styles.active : ""}`}
                      onClick={() =>
                        setValues((prev: any) => ({
                          ...prev,
                          isThirdPerson: true,
                        }))
                      }
                    >
                      Envío de un Tercero
                    </button>
                  </div>
                </div>

                {/* User Account Section (Condicional si es tercero) */}
                {values.isThirdPerson && (
                  <div className={styles.formSection}>
                    <h4 className={styles.sectionHeader}>
                      <FontAwesomeIcon
                        icon={faUser}
                        className={styles.sectionIcon}
                      />{" "}
                      Datos de quien solicita el envío
                    </h4>
                    <div className={styles.packageInfoInputs}>
                      <div className={styles.grid2Col}>
                        <div className={styles.formGroup}>
                          <label>Tu Nombre</label>
                          <input
                            type="text"
                            name="userFirstName"
                            value={values.userFirstName}
                            onChange={handleChange}
                            className={
                              errors.userFirstName ? styles.isInvalid : ""
                            }
                            placeholder="Nombre"
                            maxLength={50}
                          />
                          {errors.userFirstName && (
                            <span className={styles.invalidFeedback}>
                              {errors.userFirstName}
                            </span>
                          )}
                        </div>
                        <div className={styles.formGroup}>
                          <label>Tu Apellido</label>
                          <input
                            type="text"
                            name="userLastName"
                            value={values.userLastName}
                            onChange={handleChange}
                            className={
                              errors.userLastName ? styles.isInvalid : ""
                            }
                            placeholder="Apellido"
                            maxLength={50}
                          />
                          {errors.userLastName && (
                            <span className={styles.invalidFeedback}>
                              {errors.userLastName}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className={`${styles.grid2Col} ${styles.mt15}`}>
                        <div className={styles.formGroup}>
                          <label>Tu Email (para acceder a tu cuenta)</label>
                          <div className={styles.zipInputWrapper}>
                            <input
                              type="email"
                              name="userEmail"
                              value={values.userEmail}
                              onChange={handleChange}
                              onBlur={() =>
                                validateEmailExistence(values.userEmail)
                              }
                              className={
                                errors.userEmail ? styles.isInvalid : ""
                              }
                              placeholder="email@ejemplo.com"
                              maxLength={100}
                            />
                            {isValidatingEmail && (
                              <div className={styles.inlineLoader}>
                                <FontAwesomeIcon icon={faSpinner} spin />
                              </div>
                            )}
                          </div>
                          {errors.userEmail && (
                            <span className={styles.invalidFeedback}>
                              {errors.userEmail}
                            </span>
                          )}
                        </div>
                        <div className={styles.formGroup}>
                          <label>Tu País</label>
                          <select
                            name="userCountry"
                            value={values.userCountry}
                            onChange={handleChange}
                            className={
                              errors.userCountry ? styles.isInvalid : ""
                            }
                          >
                            <option value="">Seleccionar País</option>
                            {allCountries.map((c) => (
                              <option key={c.id} value={c.iso2Country}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                          {errors.userCountry && (
                            <span className={styles.invalidFeedback}>
                              {errors.userCountry}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Sender Section */}
                <div className={styles.formSection}>
                  <h4 className={styles.sectionHeader}>
                    <FontAwesomeIcon
                      icon={faMapMarkerAlt}
                      className={styles.sectionIcon}
                    />{" "}
                    Datos de quien envía
                  </h4>
                  <div className={styles.packageInfoInputs}>
                    <div className={styles.grid2Col}>
                      <div className={styles.formGroup}>
                        <label>País de Origen</label>
                        <select
                          name="origin"
                          value={values.origin}
                          onChange={handleChange}
                          disabled
                          className={styles.disabledSelect}
                        >
                          <option value="US">Estados Unidos</option>
                        </select>
                      </div>
                      {customerOriginAddresses.length > 0 && (
                        <div className={styles.formGroup}>
                          <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span>Mis Direcciones de Origen</span>
                            {selectedOriginAddressId && selectedOriginAddressId !== "custom" && (
                              <span style={{ fontSize: "11px", color: "#b45309", fontWeight: 700 }}>
                                {customerOriginAddresses.find((a: any) => String(a.id) === String(selectedOriginAddressId))?.isDefault ? "★ PREDETERMINADA" : "Guardada"}
                              </span>
                            )}
                          </label>
                          <select
                            value={selectedOriginAddressId}
                            onChange={(e) => handleOriginAddressChange(e.target.value)}
                            style={{
                              fontWeight: 500,
                              borderColor: (selectedOriginAddressId !== "custom" && customerOriginAddresses.find((a: any) => String(a.id) === String(selectedOriginAddressId))?.isDefault) ? "#f59e0b" : undefined
                            }}
                          >
                            {customerOriginAddresses.map((addr: any) => (
                              <option key={addr.id} value={addr.id}>
                                {getFormattedAddressLabel(addr)}
                              </option>
                            ))}
                            <option value="custom">✏️ Ingresar otra dirección manualmente...</option>
                          </select>
                        </div>
                      )}
                    </div>
                    <div className={`${styles.grid2Col} ${styles.mt15}`}>
                      <div className={styles.formGroup}>
                        <label>Nombre</label>
                        <input
                          type="text"
                          name="senderFirstName"
                          value={values.senderFirstName}
                          onChange={handleChange}
                          className={
                            errors.senderFirstName ? styles.isInvalid : ""
                          }
                          placeholder="Nombre"
                          maxLength={50}
                        />
                        {errors.senderFirstName && (
                          <span className={styles.invalidFeedback}>
                            {errors.senderFirstName}
                          </span>
                        )}
                      </div>
                      <div className={styles.formGroup}>
                        <label>Apellido</label>
                        <input
                          type="text"
                          name="senderLastName"
                          value={values.senderLastName}
                          onChange={handleChange}
                          className={
                            errors.senderLastName ? styles.isInvalid : ""
                          }
                          placeholder="Apellido"
                          maxLength={50}
                        />
                        {errors.senderLastName && (
                          <span className={styles.invalidFeedback}>
                            {errors.senderLastName}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className={`${styles.grid2Col} ${styles.mt15}`}>
                      <div className={styles.formGroup}>
                        <label>Email</label>
                        <div className={styles.zipInputWrapper}>
                          <input
                            type="email"
                            name="senderEmail"
                            value={values.senderEmail}
                            onChange={handleChange}
                            onBlur={() => {
                              if (!values.isThirdPerson) {
                                validateEmailExistence(values.senderEmail);
                              }
                            }}
                            className={
                              errors.senderEmail ? styles.isInvalid : ""
                            }
                            placeholder="email@ejemplo.com"
                            maxLength={100}
                          />
                          {!values.isThirdPerson && isValidatingEmail && (
                            <div className={styles.inlineLoader}>
                              <FontAwesomeIcon icon={faSpinner} spin />
                            </div>
                          )}
                        </div>
                        {errors.senderEmail && (
                          <span className={styles.invalidFeedback}>
                            {errors.senderEmail}
                          </span>
                        )}
                      </div>
                      <div className={styles.formGroup}>
                        <label>Teléfono</label>
                        <div
                          className={`${styles.phoneInputWrapper} ${errors.senderPhone ? styles.isInvalidWrapper : ""
                            }`}
                        >
                          <div className={styles.phonePrefix}>
                            <img
                              src="https://flagcdn.com/w40/us.png"
                              srcSet="https://flagcdn.com/w80/us.png 2x"
                              width="20"
                              height="14"
                              alt="Estados Unidos"
                              className={styles.phonePrefixFlag}
                              loading="lazy"
                              decoding="async"
                            />
                            <span className={styles.phonePrefixCode}>+1</span>
                          </div>
                          <input
                            type="text"
                            name="senderPhone"
                            value={values.senderPhone}
                            onChange={handleSenderPhoneChange}
                            placeholder={getPhonePlaceholder(values.origin)}
                            maxLength={15}
                          />
                        </div>
                        {errors.senderPhone && (
                          <span className={styles.invalidFeedback}>
                            {errors.senderPhone}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className={`${findDniTypeByCompositeKey(senderDniTypes, values.senderKindOfDNI)?.requiereRazonSocial === true ? styles.gridDni3Col : styles.gridDni} ${styles.mt15}`}>
                      <div className={styles.formGroup}>
                        <label>Tipo Doc.</label>
                        <select
                          name="senderKindOfDNI"
                          value={values.senderKindOfDNI}
                          onChange={handleSenderKindOfDniChange}
                          className={
                            errors.senderKindOfDNI ? styles.isInvalid : ""
                          }
                        >
                          <option value="">Seleccione...</option>
                          {senderDniTypes.map((t) => (
                            <option key={getDniCompositeKey(t)} value={getDniCompositeKey(t)}>
                              {t.prefix || t.kindOfDNI}
                            </option>
                          ))}
                        </select>
                        {errors.senderKindOfDNI && (
                          <span className={styles.invalidFeedback}>
                            {errors.senderKindOfDNI}
                          </span>
                        )}
                      </div>
                      <div className={styles.formGroup}>
                        <label>Documento</label>
                        <input
                          type="text"
                          name="senderDNI"
                          value={values.senderDNI}
                          onChange={handleSenderDniChange}
                          className={errors.senderDNI ? styles.isInvalid : ""}
                          placeholder="Nro documento"
                          maxLength={getMaxLengthFromMask(findDniTypeByCompositeKey(senderDniTypes, values.senderKindOfDNI)?.mask) || 20}
                        />
                        {errors.senderDNI && (
                          <span className={styles.invalidFeedback}>
                            {errors.senderDNI}
                          </span>
                        )}
                      </div>
                      {findDniTypeByCompositeKey(senderDniTypes, values.senderKindOfDNI)?.requiereRazonSocial === true && (
                        <div className={styles.formGroup}>
                          <label>Razón Social</label>
                          <input
                            type="text"
                            name="senderRazonSocial"
                            value={values.senderRazonSocial}
                            onChange={handleChange}
                            className={errors.senderRazonSocial ? styles.isInvalid : ""}
                            placeholder="Nombre de la empresa o razón social"
                          />
                          {errors.senderRazonSocial && (
                            <span className={styles.invalidFeedback}>
                              {errors.senderRazonSocial}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className={`${styles.grid2Col} ${styles.mt15}`}>
                      <div className={styles.formGroup}>
                        <label>Dirección</label>
                        {GOOGLE_API_KEY ? (
                          <AddressAutocomplete
                            value={values.senderAddressLine1}
                            onChange={(value) => {
                              setValues((prev: any) => ({
                                ...prev,
                                senderAddressLine1: value,
                                senderCity: "",
                                senderState: "",
                                originZip: "",
                              }));
                              setZipValidatedByField((prev) => ({
                                ...prev,
                                originZip: false,
                              }));
                            }}
                            onAddressSelect={handleSenderAddressSelect}
                            placeholder="Calle, Av, Edificio..."
                            inputName="senderAddressLine1"
                            apiKey={GOOGLE_API_KEY}
                            inputClassName={
                              errors.senderAddressLine1 ? styles.isInvalid : ""
                            }
                            country={values.origin.toLowerCase()}
                          />
                        ) : (
                          <input
                            type="text"
                            name="senderAddressLine1"
                            value={values.senderAddressLine1}
                            onChange={handleChange}
                            className={
                              errors.senderAddressLine1 ? styles.isInvalid : ""
                            }
                            placeholder="Calle, Av, Edificio..."
                            maxLength={150}
                          />
                        )}
                        {errors.senderAddressLine1 && (
                          <span className={styles.invalidFeedback}>
                            {errors.senderAddressLine1}
                          </span>
                        )}
                      </div>
                      <div className={styles.formGroup}>
                        <label>Referencia (Apto, Suite, Piso, Nro. Casa...)</label>
                        <input
                          type="text"
                          name="senderAddressLine2"
                          value={values.senderAddressLine2}
                          onChange={handleChange}
                          placeholder="Apto, Suite, Piso, Nro. Casa... (opcional)"
                          maxLength={100}
                        />
                      </div>
                    </div>
                    <div className={`${styles.grid3Col} ${styles.mt15}`}>
                      <div className={styles.formGroup}>
                        <label>Ciudad</label>
                        <input
                          type="text"
                          name="senderCity"
                          value={values.senderCity}
                          onChange={handleChange}
                          className={errors.senderCity ? styles.isInvalid : ""}
                          maxLength={50}
                        />
                        {errors.senderCity && (
                          <span className={styles.invalidFeedback}>
                            {errors.senderCity}
                          </span>
                        )}
                      </div>
                      <div className={styles.formGroup}>
                        <label>Estado</label>
                        <input
                          type="text"
                          name="senderState"
                          value={values.senderState}
                          onChange={handleChange}
                          className={errors.senderState ? styles.isInvalid : ""}
                          maxLength={50}
                        />
                        {errors.senderState && (
                          <span className={styles.invalidFeedback}>
                            {errors.senderState}
                          </span>
                        )}
                      </div>
                      <div className={styles.formGroup}>
                        <label>Código Postal</label>
                        <div className={styles.zipInputWrapper}>
                          <input
                            type="text"
                            name="originZip"
                            value={values.originZip}
                            onChange={(e) => {
                              handleChange(e);
                              setZipValidatedByField((prev) => ({
                                ...prev,
                                originZip: false,
                              }));
                            }}
                            onBlur={() => validateZipCode("originZip")}
                            className={errors.originZip ? styles.isInvalid : ""}
                            maxLength={10}
                          />
                          {isValidatingZip && (
                            <div className={styles.inlineLoader}>
                              <FontAwesomeIcon icon={faSpinner} spin />
                            </div>
                          )}
                        </div>
                        {errors.originZip && (
                          <span className={styles.invalidFeedback}>
                            {errors.originZip}
                          </span>
                        )}
                      </div>
                    </div>

                  </div>
                </div>

                <div className={styles.stepActions}>
                  <button
                    className={styles.cancelShipmentBtn}
                    onClick={() => setCurrentStep(1)}
                  >
                    Atrás
                  </button>
                  <button
                    className={styles.processShipmentBtn}
                    onClick={handleNextStep}
                  >
                    Siguiente <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Receiver Details */}
            {currentStep === 3 && (
              <div className="fade-in">
                <h3 className={styles.stepHeader}>Datos del Destinatario</h3>

                {/* Receiver Section */}
                <div className={styles.formSection}>
                  <h4 className={styles.sectionHeader}>
                    <FontAwesomeIcon
                      icon={faTruck}
                      className={styles.sectionIcon}
                    />{" "}
                    Datos de quien recibe
                  </h4>
                  <div className={styles.packageInfoInputs}>
                    <div className={styles.grid2Col}>
                      <div className={styles.formGroup} style={{ position: "relative" }}>
                        <label>País de Destino</label>
                        <select
                          name="destination"
                          value={values.destination}
                          className={errors.destination ? styles.isInvalid : ""}
                          onChange={(e) => {
                            handleChange(e);
                            setValues((prev: any) => ({
                              ...prev,
                              receiverFirstName: "",
                              receiverLastName: "",
                              receiverEmail: "",
                              receiverPhone: "",
                              receiverKindOfDNI: "",
                              receiverDNI: "",
                              receiverRazonSocial: "",
                              receiverAddressLine1: "",
                              receiverAddressLine2: "",
                              receiverCity: "",
                              receiverState: "",
                              destinationZip: "",
                              municipalityDestiny: "",
                            }));
                            setZipValidatedByField((prev) => ({
                              ...prev,
                              destinationZip: false,
                            }));
                            setReceiverAddress("");
                            setErrors((prev: any) => ({
                              ...prev,
                              receiverFirstName: undefined,
                              receiverLastName: undefined,
                              receiverEmail: undefined,
                              receiverPhone: undefined,
                              receiverKindOfDNI: undefined,
                              receiverDNI: undefined,
                              receiverRazonSocial: undefined,
                              receiverAddressLine1: undefined,
                              receiverAddressLine2: undefined,
                              receiverCity: undefined,
                              receiverState: undefined,
                              destinationZip: undefined,
                            }));
                          }}
                        >
                          <option value="">Seleccionar País</option>
                          {receiverCountries.map((c) => (
                            <option key={c.id} value={c.iso2Country}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                        {errors.destination && (
                          <span className={styles.invalidFeedback} style={{ position: "absolute", top: "100%", left: 0, display: "block", marginTop: "2px", fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                            {errors.destination}
                          </span>
                        )}
                      </div>
                      {values.destination && availableDestAddresses.length > 0 && (
                        <div className={styles.formGroup}>
                          <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span>Mis Direcciones de Destino</span>
                            {selectedDestAddressId && selectedDestAddressId !== "custom" && (
                              <span style={{ fontSize: "11px", color: "#b45309", fontWeight: 700 }}>
                                {availableDestAddresses.find((a: any) => String(a.id) === String(selectedDestAddressId))?.isDefault ? "★ PREDETERMINADA" : "Guardada"}
                              </span>
                            )}
                          </label>
                          <select
                            value={selectedDestAddressId}
                            onChange={(e) => handleDestAddressChange(e.target.value)}
                            style={{
                              fontWeight: 500,
                              borderColor: (selectedDestAddressId !== "custom" && availableDestAddresses.find((a: any) => String(a.id) === String(selectedDestAddressId))?.isDefault) ? "#f59e0b" : undefined
                            }}
                          >
                            <option value="custom">✏️ Ingresar dirección manualmente...</option>
                            {availableDestAddresses.map((addr: any) => (
                              <option key={addr.id} value={addr.id}>
                                {getFormattedAddressLabel(addr)}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                    <div className={`${styles.grid2Col} ${styles.mt15}`}>
                      <div className={styles.formGroup}>
                        <label>Nombre</label>
                        <input
                          type="text"
                          name="receiverFirstName"
                          value={values.receiverFirstName}
                          onChange={handleChange}
                          disabled={!values.destination}
                          className={
                            errors.receiverFirstName ? styles.isInvalid : ""
                          }
                          placeholder={!values.destination ? "Selecciona un país primero" : "Nombre"}
                          style={!values.destination ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                          maxLength={50}
                        />
                        {errors.receiverFirstName && (
                          <span className={styles.invalidFeedback}>
                            {errors.receiverFirstName}
                          </span>
                        )}
                      </div>
                      <div className={styles.formGroup}>
                        <label>Apellido</label>
                        <input
                          type="text"
                          name="receiverLastName"
                          value={values.receiverLastName}
                          onChange={handleChange}
                          disabled={!values.destination}
                          className={
                            errors.receiverLastName ? styles.isInvalid : ""
                          }
                          placeholder={!values.destination ? "Selecciona un país primero" : "Apellido"}
                          style={!values.destination ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                          maxLength={50}
                        />
                        {errors.receiverLastName && (
                          <span className={styles.invalidFeedback}>
                            {errors.receiverLastName}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className={`${styles.grid2Col} ${styles.mt15}`}>
                      <div className={styles.formGroup}>
                        <label>Email</label>
                        <input
                          type="email"
                          name="receiverEmail"
                          value={values.receiverEmail}
                          onChange={handleChange}
                          disabled={!values.destination}
                          className={
                            errors.receiverEmail ? styles.isInvalid : ""
                          }
                          placeholder={!values.destination ? "Selecciona un país primero" : "correo@ejemplo.com"}
                          style={!values.destination ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                          maxLength={100}
                        />
                        {errors.receiverEmail && (
                          <span className={styles.invalidFeedback}>
                            {errors.receiverEmail}
                          </span>
                        )}
                      </div>
                      <div className={styles.formGroup}>
                        <label>Teléfono</label>
                        <div
                          className={`${styles.phoneInputWrapper} ${errors.receiverPhone ? styles.isInvalidWrapper : ""
                            }`}
                          style={!values.destination ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                        >
                          {values.destination && (
                            <div className={styles.phonePrefix}>
                              <img
                                src={`https://flagcdn.com/w40/${values.destination.toLowerCase()}.png`}
                                srcSet={`https://flagcdn.com/w80/${values.destination.toLowerCase()}.png 2x`}
                                width="20"
                                height="14"
                                alt={values.destination}
                                className={styles.phonePrefixFlag}
                                loading="lazy"
                                decoding="async"
                              />
                              {selectedReceiverCountry?.phoneCode && (
                                <span className={styles.phonePrefixCode}>
                                  {selectedReceiverCountry.phoneCode}
                                </span>
                              )}
                            </div>
                          )}
                          <input
                            type="text"
                            name="receiverPhone"
                            value={values.receiverPhone}
                            onChange={handleReceiverPhoneChange}
                            disabled={!values.destination}
                            placeholder={!values.destination ? "Selecciona un país primero" : `Ej: ${getPhonePlaceholder(values.destination)}`}
                            maxLength={15}
                          />
                        </div>
                        {errors.receiverPhone && (
                          <span className={styles.invalidFeedback}>
                            {errors.receiverPhone}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className={`${findDniTypeByCompositeKey(receiverDniTypes, values.receiverKindOfDNI)?.requiereRazonSocial === true ? styles.gridDni3Col : styles.gridDni} ${styles.mt15}`}>
                      <div className={styles.formGroup}>
                        <label>Tipo Doc.</label>
                        <select
                          name="receiverKindOfDNI"
                          value={values.receiverKindOfDNI}
                          onChange={handleReceiverKindOfDniChange}
                          className={
                            errors.receiverKindOfDNI ? styles.isInvalid : ""
                          }
                        >
                          <option value="">Seleccione...</option>
                          {receiverDniTypes.map((t) => (
                            <option key={getDniCompositeKey(t)} value={getDniCompositeKey(t)}>
                              {t.prefix || t.kindOfDNI}
                            </option>
                          ))}
                        </select>
                        {errors.receiverKindOfDNI && (
                          <span className={styles.invalidFeedback}>
                            {errors.receiverKindOfDNI}
                          </span>
                        )}
                      </div>
                      <div className={styles.formGroup}>
                        <label>Documento</label>
                        <input
                          type="text"
                          name="receiverDNI"
                          value={values.receiverDNI}
                          onChange={handleReceiverDniChange}
                          className={errors.receiverDNI ? styles.isInvalid : ""}
                          placeholder="Nro documento"
                          maxLength={getMaxLengthFromMask(findDniTypeByCompositeKey(receiverDniTypes, values.receiverKindOfDNI)?.mask) || 20}
                        />
                        {errors.receiverDNI && (
                          <span className={styles.invalidFeedback}>
                            {errors.receiverDNI}
                          </span>
                        )}
                      </div>
                      {findDniTypeByCompositeKey(receiverDniTypes, values.receiverKindOfDNI)?.requiereRazonSocial === true && (
                        <div className={styles.formGroup}>
                          <label>Razón Social</label>
                          <input
                            type="text"
                            name="receiverRazonSocial"
                            value={values.receiverRazonSocial}
                            onChange={handleChange}
                            placeholder="Nombre de la empresa o razón social"
                          />
                        </div>
                      )}
                    </div>
                    <div className={`${styles.grid2Col} ${styles.mt15}`}>
                      <div className={styles.formGroup}>
                        <label>Dirección</label>
                        {GOOGLE_API_KEY ? (
                          <AddressAutocomplete
                            value={values.receiverAddressLine1}
                            onChange={(value) => {
                              setValues((prev: any) => ({
                                ...prev,
                                receiverAddressLine1: value,
                                receiverCity: "",
                                receiverState: "",
                                destinationZip: "",
                              }));
                              setZipValidatedByField((prev) => ({
                                ...prev,
                                destinationZip: false,
                              }));
                            }}
                            onAddressSelect={handleReceiverAddressSelect}
                            placeholder={!values.destination ? "Selecciona un país primero" : "Calle, Av, Ciudad..."}
                            inputName="receiverAddressLine1"
                            apiKey={GOOGLE_API_KEY}
                            inputClassName={
                              errors.receiverAddressLine1 ? styles.isInvalid : ""
                            }
                            country={values.destination.toLowerCase()}
                            disabled={!values.destination}
                          />
                        ) : (
                          <input
                            type="text"
                            name="receiverAddressLine1"
                            value={values.receiverAddressLine1}
                            onChange={handleChange}
                            disabled={!values.destination}
                            className={
                              errors.receiverAddressLine1 ? styles.isInvalid : ""
                            }
                            placeholder={!values.destination ? "Selecciona un país primero" : "Calle, Av, Ciudad..."}
                            style={!values.destination ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                            maxLength={150}
                          />
                        )}
                        {errors.receiverAddressLine1 && (
                          <span className={styles.invalidFeedback}>
                            {errors.receiverAddressLine1}
                          </span>
                        )}
                      </div>
                      <div className={styles.formGroup}>
                        <label>Referencia (Apto, Suite, Piso, N. Casa...)</label>
                        <input
                          type="text"
                          name="receiverAddressLine2"
                          value={values.receiverAddressLine2}
                          onChange={handleChange}
                          disabled={!values.destination}
                          placeholder={!values.destination ? "Selecciona un país primero" : "Apto, Suite, Piso, N. Casa... (opcional)"}
                          style={!values.destination ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                          maxLength={100}
                        />
                      </div>
                    </div>
                    <div className={`${styles.grid3Col} ${styles.mt15}`}>
                      <div className={styles.formGroup}>
                        <label>Ciudad</label>
                        <input
                          type="text"
                          name="receiverCity"
                          value={values.receiverCity}
                          onChange={handleChange}
                          className={
                            errors.receiverCity ? styles.isInvalid : ""
                          }
                          maxLength={50}
                        />
                        {errors.receiverCity && (
                          <span className={styles.invalidFeedback}>
                            {errors.receiverCity}
                          </span>
                        )}
                      </div>
                      <div className={styles.formGroup}>
                        <label>Estado</label>
                        <input
                          type="text"
                          name="receiverState"
                          value={values.receiverState}
                          onChange={handleChange}
                          className={
                            errors.receiverState ? styles.isInvalid : ""
                          }
                          maxLength={50}
                        />
                        {errors.receiverState && (
                          <span className={styles.invalidFeedback}>
                            {errors.receiverState}
                          </span>
                        )}
                      </div>
                      <div className={styles.formGroup}>
                        <label>Código Postal</label>
                        <div className={styles.zipInputWrapper}>
                          <input
                            type="text"
                            name="destinationZip"
                            value={values.destinationZip}
                            onChange={(e) => {
                              handleChange(e);
                              setZipValidatedByField((prev) => ({
                                ...prev,
                                destinationZip: false,
                              }));
                            }}
                            onBlur={() => validateZipCode("destinationZip")}
                            className={
                              errors.destinationZip ? styles.isInvalid : ""
                            }
                            maxLength={10}
                          />
                          {isValidatingZip && (
                            <div className={styles.inlineLoader}>
                              <FontAwesomeIcon icon={faSpinner} spin />
                            </div>
                          )}
                        </div>
                        {errors.destinationZip && (
                          <span className={styles.invalidFeedback}>
                            {errors.destinationZip}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className={styles.stepActions}>
                  <button
                    className={styles.cancelShipmentBtn}
                    onClick={() => setCurrentStep(2)}
                  >
                    Atrás
                  </button>
                  <button
                    className={styles.processShipmentBtn}
                    onClick={handleNextStep}
                  >
                    Siguiente <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
              </div>
            )}

            {currentStep === 4 && (
              <div className={`${styles.resultsSection} fade-in`}>
                {isCalculating && (
                  <div className={styles.isCalculatingOverlay}>
                    <div className={styles.spinner}></div>
                    <p>Calculando tarifas...</p>
                  </div>
                )}

                {rateResults.length > 0 ? (
                  <>
                    <div className={styles.carrierSelectionContainer}>
                      {values.origin === values.destination ? (
                        /* Modo Nacional Left Column */
                        <div className={styles.mainCarriersCol}>
                          <h4>
                            Couriers disponibles para
                            <span style={{ display: 'block', fontSize: '1.3em', fontWeight: 800, marginTop: '2px', color: 'var(--brand-black)' }}>
                              {getCountryName(values.destination)}
                            </span>
                          </h4>
                          <div className={styles.servicesGrid}>
                            <ServiceCard
                              rateOption={rateResults[0]}
                              isSelected={true}
                              cleanCard={true}
                              estimatedTotal={0}
                              selectedExternalIndices={new Set()}
                              onSelectExternalService={() => {}}
                              onViewDetails={() => {}}
                              onClick={() => {}}
                            />
                          </div>
                        </div>
                      ) : (
                        /* Modo Internacional Left Column */
                        <div className={styles.mainCarriersCol}>
                          <h4>
                            Couriers disponibles para
                            <span style={{ display: 'block', fontSize: '1.3em', fontWeight: 800, marginTop: '2px', color: 'var(--brand-black)' }}>
                              {getCountryName(values.destination)}
                            </span>
                          </h4>
                          {rateResults.length > 1 && (
                            <div style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              background: 'rgba(255, 193, 20, 0.08)',
                              border: '1.5px dashed var(--elPaket-primary-color, #ffc114)',
                              borderRadius: '10px',
                              padding: '8px 14px',
                              fontSize: '0.875rem',
                              fontWeight: 600,
                              color: 'var(--brand-black)',
                              margin: '4px 0 16px'
                            }}>
                              <FontAwesomeIcon icon={faHandPointer} style={{ color: 'var(--elPaket-primary-color, #ffc114)' }} />
                              Selecciona el Courier de tu preferencia
                            </div>
                          )}
                          <div className={styles.servicesGrid}>
                            {rateResults.map((rate, idx) => {
                              const estimatedTotal = rateService.calculateTotal(
                                rate,
                                selectedExternalIndicesByCarrier.get(getServiceKey(rate)) || rateService.getInitialExternalIndices(rate),
                              );
                              const isSelected = values.selectedService === rate.carrierName && values.idCarrierService === rate.idCarrierService;
                              return (
                                <ServiceCard
                                  key={idx}
                                  rateOption={rate}
                                  estimatedTotal={estimatedTotal}
                                  isSelected={isSelected}
                                  onClick={() => {
                                    const updated = {
                                      ...values,
                                      selectedService: rate.carrierName,
                                      idCarrier: rate.idCarrier || 1,
                                      idCarrierService: rate.idCarrierService ?? undefined,
                                    };
                                    setValues(updated);
                                    fetchExternalRatesHelper(updated);
                                  }}
                                  selectedExternalIndices={selectedExternalIndicesByCarrier.get(getServiceKey(rate)) || new Set()}
                                  onSelectExternalService={(index) => selectExternalService(rate, index)}
                                  isCurrentSelectedService={false}
                                  onViewDetails={() => { setSelectedRateForModal(rate); setShowServiceDetailModal(true); }}
                                  estimatedArrivalText={getEstimatedArrivalText(rate)}
                                />
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Right Column / External Services */}
                      {values.origin === values.destination ? (
                        /* Modo Nacional Right Column */
                        <div className={styles.externalServicesCol}>
                            {(() => {
                               const activeProvider = externalServices.find(p => p.providerName?.toLowerCase() === 'ups') || externalServices[0];
                               if (!activeProvider?.elpaketDispatcherService && !activeProvider?.pickUpService) return null;
                               return (
                                  <div className={additionalStyles.pickupCardSection} style={{ marginBottom: '20px' }}>
                                    <h5 style={{ fontSize: '1.15rem', fontWeight: 700, margin: '0 0 8px', color: 'var(--brand-black)' }}>Drop Off / Pickup</h5>
                                    <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
                                      Selecciona cómo nos harás llegar tu paquete.
                                    </p>
                                  <div className={styles.pickupGrid}>
                                    {(activeProvider?.providerName?.toLowerCase().includes("ups") || false) && (
                                      <div
                                        className={`${additionalStyles.cardContainer} ${(!pickupSelected && !elpaketDispatcherSelected) ? additionalStyles.selected : ""}`}
                                        onClick={() => {
                                          setPickupSelected(false);
                                          setElpaketDispatcherSelected(false);
                                          userWantsPickupRef.current = false;
                                          userWantsElpaketDispatcherRef.current = false;
                                        }}
                                        style={{ cursor: 'pointer' }}
                                      >
                                        <div className={additionalStyles.cardMainLayout}>
                                          <div className={additionalStyles.textContent}>
                                            <div className={additionalStyles.topRow}>
                                              <div className={additionalStyles.contentRow}>
                                                <div className={additionalStyles.checkWrapper}>
                                                  {(!pickupSelected && !elpaketDispatcherSelected) ? (
                                                    <FontAwesomeIcon icon={faCheckSquare} className={additionalStyles.checkIconActive} />
                                                  ) : (
                                                    <FontAwesomeIcon icon={faSquare} className={additionalStyles.checkIconInactive} />
                                                  )}
                                                </div>
                                                <span className={additionalStyles.serviceName}>DROP OFF</span>
                                              </div>
                                              <div className={additionalStyles.freeBadge}>
                                                INCLUIDO
                                              </div>
                                            </div>

                                            <div className={additionalStyles.priceContainer}>
                                              <div className={additionalStyles.priceRow}>
                                                <span className={additionalStyles.priceValue}>$0.00</span>
                                                <span className={additionalStyles.currency}>USD</span>
                                              </div>
                                              <span className={additionalStyles.subtitleText}>
                                                Entregar el paquete en la tienda UPS de su preferencia.
                                              </span>
                                            </div>
                                          </div>

                                          <div className={additionalStyles.illustrationWrapper}>
                                            <img src={dropoffStoreImg} alt="Tienda UPS Drop Off" className={additionalStyles.cardIllustration} />
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                   {activeProvider.elpaketDispatcherService ? (
                                     <AdditionalExternalServiceCard
                                       pickupDate={values.pickupDate}
                                       onDateChange={handleChange}
                                       minDate={getTomorrowDate()}
                                       maxDate={getMaxDate()}
                                       service={{
                                         serviceName: activeProvider.elpaketDispatcherService.serviceName,
                                         serviceQty: activeProvider.elpaketDispatcherService.serviceQty,
                                         servicePrice: activeProvider.elpaketDispatcherService.servicePrice,
                                         serviceSubTotal: activeProvider.elpaketDispatcherService.serviceSubTotal,
                                         idExternalCourierService: activeProvider.elpaketDispatcherService.idExternalCourierService,
                                         transitDays: activeProvider.elpaketDispatcherService.transitDays || "N/A",
                                         standardPrice: activeProvider.elpaketDispatcherService.standardPrice,
                                         discountPercent: activeProvider.elpaketDispatcherService.discountPercent,
                                       }}
                                       isSelected={elpaketDispatcherSelected}
                                       onClick={() => {
                                         setElpaketDispatcherSelected(true);
                                         setPickupSelected(false);
                                         userWantsPickupRef.current = false;
                                         userWantsElpaketDispatcherRef.current = true;
                                       }}
                                       currency={currentSelectedRate?.currency || "USD"}
                                     />
                                   ) : activeProvider.pickUpService ? (
                                     <AdditionalExternalServiceCard
                                       pickupDate={values.pickupDate}
                                       onDateChange={handleChange}
                                       minDate={getTomorrowDate()}
                                       maxDate={getMaxDate()}
                                       service={{
                                         serviceName: activeProvider.pickUpService.serviceName,
                                         serviceQty: activeProvider.pickUpService.serviceQty,
                                         servicePrice: activeProvider.pickUpService.servicePrice,
                                         serviceSubTotal: activeProvider.pickUpService.serviceSubTotal,
                                         idExternalCourierService: activeProvider.pickUpService.idExternalCourierService,
                                         transitDays: activeProvider.pickUpService.transitDays || "N/A",
                                         standardPrice: activeProvider.pickUpService.standardPrice,
                                         discountPercent: activeProvider.pickUpService.discountPercent,
                                       }}
                                       isSelected={pickupSelected}
                                       onClick={() => {
                                         setPickupSelected(true);
                                         setElpaketDispatcherSelected(false);
                                         userWantsPickupRef.current = true;
                                         userWantsElpaketDispatcherRef.current = false;
                                       }}
                                       currency={currentSelectedRate?.currency || "USD"}
                                     />
                                   ) : null}
                                 </div>
                                 </div>
                               );
                             })()}

                          {!elpaketDispatcherSelected && (
                            <div className={styles.externalServicesWrapper}>
                              <h5>Traslado Nacional - Servicios disponibles ({rateResults[0]?.carrierName || "UPS"})</h5>
                              <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                                Selecciona obligatoriamente un servicio de envío para tu etiqueta.
                              </p>
                              {rateResults[0]?.services && rateResults[0].services.length > 0 && (
                                <div className={styles.externalCardsGrid} style={{ marginBottom: '24px' }}>
                                  {(() => {
                                    const services = [...rateResults[0].services].sort((a, b) => a.serviceSubTotal - b.serviceSubTotal);
                                    const minPrice = services[0]?.serviceSubTotal ?? 0;
                                    return services.map((option, optIdx) => {
                                      let badgeType: "lowest" | "recommended" | "express" | null = null;
                                      if (option.transitDays === "1") {
                                        badgeType = "express";
                                      } else if (option.serviceSubTotal === minPrice && services.length > 1) {
                                        badgeType = "lowest";
                                      } else if (
                                        option.serviceName.toLowerCase().includes("2nd day") ||
                                        option.serviceName.toLowerCase().includes("air")
                                      ) {
                                        badgeType = "recommended";
                                      }
                                      const isSelected =
                                        values.idCarrierService === option.idCarrierService ||
                                        (!values.idCarrierService && optIdx === 0);
                                      return (
                                        <ExternalServiceCard
                                          key={optIdx}
                                          service={{
                                            serviceName: option.serviceName,
                                            serviceQty: option.serviceQty,
                                            servicePrice: option.servicePrice,
                                            serviceSubTotal: option.serviceSubTotal,
                                            idExternalCourierService: option.idCarrierService || optIdx,
                                            transitDays: option.transitDays || "4",
                                            estimatedDeliveryDate: option.estimatedDeliveryDate,
                                            standardPrice: option.standardPrice,
                                            discountPercent: option.discountPercent,
                                          }}
                                          isSelected={isSelected}
                                          onClick={() => {
                                            const updated = {
                                              ...values,
                                              selectedService: rateResults[0].carrierName,
                                              idCarrier: rateResults[0].idCarrier || 1,
                                              idCarrierService: option.idCarrierService ?? undefined,
                                            };
                                            setValues(updated);
                                            fetchExternalRatesHelper(updated);
                                          }}
                                          currency={rateResults[0].currency || "USD"}
                                          badgeType={badgeType}
                                        />
                                      );
                                    });
                                  })()}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        /* Modo Internacional Right Column */
                        values.selectedService && (
                          <div className={styles.externalServicesCol}>
                            {!loadingExternal && externalServices.length > 0 && (() => {
                                    const activeProvider = externalServices.find(p => p.providerName?.toLowerCase() === 'ups') || externalServices[0];
                                    if (!activeProvider?.elpaketDispatcherService && !activeProvider?.pickUpService) return null;
                                    return (
                                      <div className={additionalStyles.pickupCardSection} style={{ marginBottom: '20px' }}>
                                        <h5 style={{ fontSize: '1.15rem', fontWeight: 700, margin: '0 0 8px', color: 'var(--brand-black)' }}>Drop Off / Pickup</h5>
                                        <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
                                          Selecciona cómo nos harás llegar tu paquete.
                                        </p>
                                      <div className={styles.pickupGrid}>
                                        {(activeProvider?.providerName?.toLowerCase().includes("ups") || false) && (
                                          <div
                                            className={`${additionalStyles.cardContainer} ${(!pickupSelected && !elpaketDispatcherSelected) ? additionalStyles.selected : ""}`}
                                            onClick={() => {
                                              setPickupSelected(false);
                                              setElpaketDispatcherSelected(false);
                                              userWantsPickupRef.current = false;
                                              userWantsElpaketDispatcherRef.current = false;
                                            }}
                                            style={{ cursor: 'pointer' }}
                                          >
                                            <div className={additionalStyles.cardMainLayout}>
                                              <div className={additionalStyles.textContent}>
                                                <div className={additionalStyles.topRow}>
                                                  <div className={additionalStyles.contentRow}>
                                                    <div className={additionalStyles.checkWrapper}>
                                                      {(!pickupSelected && !elpaketDispatcherSelected) ? (
                                                        <FontAwesomeIcon icon={faCheckSquare} className={additionalStyles.checkIconActive} />
                                                      ) : (
                                                        <FontAwesomeIcon icon={faSquare} className={additionalStyles.checkIconInactive} />
                                                      )}
                                                    </div>
                                                    <span className={additionalStyles.serviceName}>DROP OFF</span>
                                                  </div>
                                                  <div className={additionalStyles.freeBadge}>
                                                    INCLUIDO
                                                  </div>
                                                </div>

                                                <div className={additionalStyles.priceContainer}>
                                                  <div className={additionalStyles.priceRow}>
                                                    <span className={additionalStyles.priceValue}>$0.00</span>
                                                    <span className={additionalStyles.currency}>USD</span>
                                                  </div>
                                                  <span className={additionalStyles.subtitleText}>
                                                    Entregar el paquete en la tienda UPS de su preferencia.
                                                  </span>
                                                </div>
                                              </div>

                                              <div className={additionalStyles.illustrationWrapper}>
                                                <img src={dropoffStoreImg} alt="Tienda UPS Drop Off" className={additionalStyles.cardIllustration} />
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                        {activeProvider.elpaketDispatcherService ? (
                                          <AdditionalExternalServiceCard
                                            pickupDate={values.pickupDate}
                                            onDateChange={handleChange}
                                            minDate={getTomorrowDate()}
                                            maxDate={getMaxDate()}
                                            service={{
                                              serviceName: activeProvider.elpaketDispatcherService.serviceName,
                                              serviceQty: activeProvider.elpaketDispatcherService.serviceQty,
                                              servicePrice: activeProvider.elpaketDispatcherService.servicePrice,
                                              serviceSubTotal: activeProvider.elpaketDispatcherService.serviceSubTotal,
                                              idExternalCourierService: activeProvider.elpaketDispatcherService.idExternalCourierService,
                                              transitDays: activeProvider.elpaketDispatcherService.transitDays || "N/A",
                                              standardPrice: activeProvider.elpaketDispatcherService.standardPrice,
                                              discountPercent: activeProvider.elpaketDispatcherService.discountPercent,
                                            }}
                                            isSelected={elpaketDispatcherSelected}
                                            onClick={() => {
                                              setElpaketDispatcherSelected(true);
                                              setPickupSelected(false);
                                              userWantsPickupRef.current = false;
                                              userWantsElpaketDispatcherRef.current = true;
                                            }}
                                            currency={currentSelectedRate?.currency || "USD"}
                                          />
                                        ) : activeProvider.pickUpService ? (
                                          <AdditionalExternalServiceCard
                                            pickupDate={values.pickupDate}
                                            onDateChange={handleChange}
                                            minDate={getTomorrowDate()}
                                            maxDate={getMaxDate()}
                                            service={{
                                              serviceName: activeProvider.pickUpService.serviceName,
                                              serviceQty: activeProvider.pickUpService.serviceQty,
                                              servicePrice: activeProvider.pickUpService.servicePrice,
                                              serviceSubTotal: activeProvider.pickUpService.serviceSubTotal,
                                              idExternalCourierService: activeProvider.pickUpService.idExternalCourierService,
                                              transitDays: activeProvider.pickUpService.transitDays || "N/A",
                                              standardPrice: activeProvider.pickUpService.standardPrice,
                                              discountPercent: activeProvider.pickUpService.discountPercent,
                                            }}
                                            isSelected={pickupSelected}
                                            onClick={() => {
                                              setPickupSelected(true);
                                              setElpaketDispatcherSelected(false);
                                              userWantsPickupRef.current = true;
                                              userWantsElpaketDispatcherRef.current = false;
                                            }}
                                            currency={currentSelectedRate?.currency || "USD"}
                                          />
                                        ) : null}
                                      </div>
                                      </div>
                                    );
                                  })()}

                            {!elpaketDispatcherSelected && (
                              <div className={styles.externalServicesWrapper}>
                                <h5 style={{ fontWeight: 700, marginBottom: '8px' }}>Traslado Nacional - Servicios disponibles ({externalServices[0]?.providerName || 'UPS'})</h5>
                                {loadingExternal ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '20px 0' }}>
                                   <FontAwesomeIcon icon={faSpinner} spin style={{ color: 'var(--brand-yellow)' }} />
                                    <span>Cargando servicios externos...</span>
                                  </div>
                                ) : externalServices.length > 0 ? (
                                  <>
                                    <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                                      Selecciona obligatoriamente un servicio de envío para tu etiqueta.
                                    </p>
                                    <div className={styles.externalCardsGrid} style={elpaketDispatcherSelected ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
                                      {externalServices.flatMap(provider => {
                                        const sortedOpts = [...(provider.options || [])].sort((a, b) => a.serviceSubTotal - b.serviceSubTotal);
                                        const allOptions = externalServices.flatMap(p => p.options || []);
                                        const minPrice = allOptions.length > 0 ? Math.min(...allOptions.map(o => o.serviceSubTotal)) : 0;
                                        return sortedOpts.map((option, optIdx) => {
                                          let badgeType: 'lowest' | 'recommended' | 'express' | null = null;
                                          if (option.transitDays === "1") {
                                            badgeType = 'express';
                                          } else if (option.serviceSubTotal === minPrice && allOptions.length > 1) {
                                            badgeType = 'lowest';
                                          } else if (option.serviceName.toLowerCase().includes('2nd day') || option.serviceName.toLowerCase().includes('air')) {
                                            badgeType = 'recommended';
                                          }
                                          return (
                                            <ExternalServiceCard
                                              key={optIdx}
                                              service={{
                                                serviceName: option.serviceName,
                                                serviceQty: option.serviceQty,
                                                servicePrice: option.servicePrice,
                                                serviceSubTotal: option.serviceSubTotal,
                                                idExternalCourierService: option.idExternalCourierService,
                                                transitDays: option.transitDays || 'N/A',
                                                estimatedDeliveryDate: option.estimatedDeliveryDate,
                                                standardPrice: option.standardPrice,
                                                discountPercent: option.discountPercent,
                                              }}
                                              isSelected={!elpaketDispatcherSelected && selectedExternalOption?.idExternalCourierService === option.idExternalCourierService}
                                              onClick={() => {
                                                setSelectedExternalOption(option);
                                                setElpaketDispatcherSelected(false);
                                              }}
                                              currency={currentSelectedRate?.currency || 'USD'}
                                              badgeType={badgeType}
                                            />
                                          );
                                        });
                                      })}
                                    </div>
                                  </>
                                ) : (
                                  <p style={{ fontSize: '13px', color: '#64748b' }}>No hay servicios externos disponibles para este courier.</p>
                                )}
                              </div>
                            )}
                          </div>
                        )
                      )}
                    </div>

                    {values.selectedService && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '20px 24px',
                        background: 'var(--elPaket-primary-color-light, #eff6ff)',
                        borderRadius: '12px',
                        border: '1.5px solid var(--elPaket-primary-color)',
                        marginTop: '20px',
                        marginBottom: '20px'
                      }}>
                        <span style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--brand-black)' }}>Total estimado</span>
                        <span style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--brand-black)' }}>
                          {dynamicTotal.toFixed(2)} {currentSelectedRate?.currency || 'USD'}
                        </span>
                      </div>
                    )}

                    <div className={styles.stepActions}>
                      <button className={styles.cancelShipmentBtn} onClick={() => setCurrentStep(3)}>Atrás</button>
                      <button
                        className={styles.processShipmentBtn}
                        onClick={handleNextStep}
                        disabled={!values.selectedService || (values.origin !== values.destination && !selectedExternalOption && !elpaketDispatcherSelected) || loadingExternal}
                      >
                        {loadingExternal ? <><FontAwesomeIcon icon={faSpinner} spin /> Calculando...</> : <>Siguiente <FontAwesomeIcon icon={faChevronRight} /></>}
                      </button>
                    </div>
                  </>
                ) : (
                  !isCalculating && (
                    <div style={{
                      textAlign: 'center',
                      padding: '48px 32px',
                      borderRadius: '16px',
                      border: '1.5px solid #fee2e2',
                      backgroundColor: '#fff8f8',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '16px',
                    }}>
                      <div style={{
                        width: '64px', height: '64px', borderRadius: '50%',
                        backgroundColor: '#fee2e2', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        fontSize: '28px'
                      }}>
                        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="12" y1="8" x2="12" y2="12" />
                          <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                      </div>
                      <div>
                        <p style={{ margin: '0 0 6px', fontSize: '1rem', fontWeight: 700, color: '#1e293b' }}>
                          No encontramos couriers disponibles
                        </p>
                        <p style={{ margin: 0, fontSize: '0.875rem', color: '#64748b', lineHeight: 1.6 }}>
                          No hay opciones de envío para los parámetros seleccionados.<br />
                          Intenta ajustar el destino, las dimensiones o el método de envío.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setCurrentStep(3)}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '8px',
                          padding: '10px 22px', borderRadius: '10px',
                          border: '1.5px solid var(--elPaket-primary-color)',
                          backgroundColor: 'transparent',
                          color: 'var(--elPaket-primary-color)',
                          fontWeight: 600, fontSize: '0.875rem',
                          cursor: 'pointer', transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--elPaket-primary-color)'; (e.currentTarget as HTMLButtonElement).style.color = '#fff'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--elPaket-primary-color)'; }}
                      >
                        <FontAwesomeIcon icon={faChevronLeft} /> Volver y ajustar datos
                      </button>
                    </div>
                  )
                )}
              </div>
            )}

            {currentStep === 5 && currentSelectedRate && (
              <div className={`${styles.summarySection} fade-in`}>
                <div className={styles.summaryLayout}>
                  <div className={styles.summaryLeftColumn}>
                    <div className={styles.summaryGrid}>
                      {/* Package Summary */}
                      <div className={styles.summaryCard}>
                        <h5 style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <span><FontAwesomeIcon icon={faBox} /> Resumen de paquete</span>
                          <div
                            className={`${styles.editStepIcon} ${styles.iconPointer}`}
                            onClick={() => setShowEditPackageModal(true)}
                            title="Editar detalles del paquete"
                          >
                            <FontAwesomeIcon icon={faPencilAlt} />
                          </div>
                        </h5>
                        <div className={styles.summaryDetails}>
                          <div className={styles.detailItem}>
                            <span className={styles.label}>Tipo:</span>
                            <span className={styles.value}>
                              {values.parcelOrdDoc === "DOC" ? "Documento" : "Caja"}
                            </span>
                          </div>
                          <div className={styles.detailItem}>
                            <span className={styles.label}>Peso:</span>
                            <span className={styles.value}>
                              {values.weight}{" "}
                              {values.unitSystem === "metric" ? "kg" : "lb"}
                            </span>
                          </div>
                          <div className={styles.detailItem}>
                            <span className={styles.label}>Dimensiones:</span>
                            <span className={styles.value}>
                              {values.length}x{values.width}x{values.height}{" "}
                              {values.unitSystem === "metric" ? "cm" : "in"}
                            </span>
                          </div>
                          <div className={styles.detailItem}>
                            <span className={styles.label}>Valor FOB:</span>
                            <span className={styles.value}>
                              ${values.fob} {values.currency || "USD"}
                            </span>
                          </div>
                          <div className={styles.detailItem}>
                            <span className={styles.label}>Contenido:</span>
                            <span className={styles.value}>
                              {values.packageContent}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Solicitor Summary */}
                      {values.isThirdPerson && (
                        <div className={styles.summaryCard}>
                          <h5>
                            <span><FontAwesomeIcon icon={faUser} /> Solicitante <small style={{ fontSize: '0.75em', fontWeight: 'normal', color: '#64748b', marginLeft: '4px' }}>(Persona ordena el envío)</small></span>
                          </h5>
                          <div className={styles.summaryDetails}>
                            <div className={styles.detailItem}>
                              <span className={styles.label}>Nombre:</span>
                              <span className={styles.value}>
                                {values.userFirstName} {values.userLastName}
                              </span>
                            </div>
                            <div className={styles.detailItem}>
                              <span className={styles.label}>Email:</span>
                              <span className={styles.value}>
                                {values.userEmail}
                              </span>
                            </div>
                            <div className={styles.detailItem}>
                              <span className={styles.label}>País:</span>
                              <span className={styles.value}>
                                {getCountryName(values.userCountry)}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Sender Summary */}
                      <div className={styles.summaryCard}>
                        <h5 style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <span><FontAwesomeIcon icon={faMapMarkerAlt} /> Remitente <small style={{ fontSize: '0.75em', fontWeight: 'normal', color: '#64748b', marginLeft: '4px' }}>(Persona entrega el paquete)</small></span>
                          <div
                            className={`${styles.editStepIcon} ${styles.iconPointer}`}
                            onClick={() => setShowEditSenderModal(true)}
                            title="Editar remitente"
                          >
                            <FontAwesomeIcon icon={faPencilAlt} />
                          </div>
                        </h5>
                        <div className={styles.summaryDetails}>
                          <div className={styles.detailItem}>
                            <span className={styles.label}>Nombre:</span>
                            <span className={styles.value}>
                              {values.senderFirstName} {values.senderLastName}
                            </span>
                          </div>
                          <div className={styles.detailItem}>
                            <span className={styles.label}>Dirección:</span>
                            <span className={styles.value}>
                              {[
                                values.senderAddressLine1,
                                values.senderCity,
                                values.senderState,
                                values.originZip,
                                getCountryName(values.origin),
                              ]
                                .filter(Boolean)
                                .join(", ")}
                            </span>
                          </div>
                          <div className={styles.detailItem}>
                            <span className={styles.label}>Teléfono:</span>
                            <span className={styles.value}>
                              {selectedSenderCountry?.phoneCode
                                ? `${selectedSenderCountry.phoneCode}${values.senderPhone}`
                                : `+1${values.senderPhone}`}
                            </span>
                          </div>
                          {values.senderDNI && (
                            <div className={styles.detailItem}>
                              <span className={styles.label}>Documento:</span>
                              <span className={styles.value}>
                                {values.senderKindOfDNI && `${getDniLabel(senderDniTypes, values.senderKindOfDNI)} - `}{values.senderDNI}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Receiver Summary */}
                      <div className={styles.summaryCard}>
                        <h5 style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <span><FontAwesomeIcon icon={faTruck} /> Destinatario <small style={{ fontSize: '0.75em', fontWeight: 'normal', color: '#64748b', marginLeft: '4px' }}>(Persona recibe el paquete)</small></span>
                          <div
                            className={`${styles.editStepIcon} ${styles.iconPointer}`}
                            onClick={() => setShowEditReceiverModal(true)}
                            title="Editar destinatario"
                          >
                            <FontAwesomeIcon icon={faPencilAlt} />
                          </div>
                        </h5>
                        <div className={styles.summaryDetails}>
                          <div className={styles.detailItem}>
                            <span className={styles.label}>Nombre:</span>
                            <span className={styles.value}>
                              {values.receiverFirstName}{" "}
                              {values.receiverLastName}
                            </span>
                          </div>
                          <div className={styles.detailItem}>
                            <span className={styles.label}>Dirección:</span>
                            <span className={styles.value}>
                              {[
                                values.receiverAddressLine1,
                                values.receiverCity,
                                values.receiverState,
                                values.destinationZip,
                                getCountryName(values.destination),
                              ]
                                .filter(Boolean)
                                .join(", ")}
                            </span>
                          </div>
                          <div className={styles.detailItem}>
                            <span className={styles.label}>Teléfono:</span>
                            <span className={styles.value}>
                              {selectedReceiverCountry?.phoneCode
                                ? `${selectedReceiverCountry.phoneCode}${values.receiverPhone}`
                                : values.receiverPhone}
                            </span>
                          </div>
                          {values.receiverDNI && (
                            <div className={styles.detailItem}>
                              <span className={styles.label}>Documento:</span>
                              <span className={styles.value}>
                                {values.receiverKindOfDNI && `${getDniLabel(receiverDniTypes, values.receiverKindOfDNI)} - `}{values.receiverDNI}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={styles.summaryRightColumn}>
                    <div className={styles.costSummaryCard}>
                      <h5>
                        <FontAwesomeIcon icon={faCreditCard} /> Resumen de
                        Costos
                      </h5>
                      <div className={styles.costBreakdown}>
                        {(() => {
                          const isNat = values.origin === values.destination;
                          const nationalServices = isNat ? (rateResults[0]?.services || []) : [];
                          const selectedSubSvc = isNat
                            ? (nationalServices.find(s => s.idCarrierService === values.idCarrierService) || nationalServices[0])
                            : null;
                          const carrierAmount = isNat
                            ? (selectedSubSvc?.serviceSubTotal ?? 0)
                            : currentSelectedRate.services.reduce((acc, s) => acc + s.serviceSubTotal, 0);
                          const carrierLabel = isNat
                            ? (selectedSubSvc?.serviceName ?? currentSelectedRate.carrierName)
                            : (currentSelectedRate.serviceIndicator
                              ? `${currentSelectedRate.carrierName} - ${currentSelectedRate.serviceIndicator}`
                              : currentSelectedRate.carrierName);
                          return (
                            <div className={styles.costItem}>
                              <span>{carrierLabel}</span>
                              <span>{carrierAmount.toFixed(2)} {currentSelectedRate.currency}</span>
                            </div>
                          );
                        })()}

                        {!(values.origin === values.destination) && selectedExternalOption && !elpaketDispatcherSelected && (
                          <div className={styles.costItem}>
                            <span>{selectedExternalOption.serviceName}</span>
                            <span>{selectedExternalOption.serviceSubTotal.toFixed(2)} {currentSelectedRate.currency}</span>
                          </div>
                        )}
                        {elpaketDispatcherSelected && externalServices[0]?.elpaketDispatcherService && (
                          <>
                            <div className={styles.costItem}>
                              <span>{externalServices[0].elpaketDispatcherService.serviceName}</span>
                              <span>{externalServices[0].elpaketDispatcherService.serviceSubTotal.toFixed(2)} {currentSelectedRate.currency}</span>
                            </div>
                            <div className={styles.pickupNotice}>
                              <div>Fecha de recogida: <strong>{values.pickupDate}</strong></div>
                              <div>Horario de recogida: entre las <strong>8:00</strong> y las <strong>17:00</strong></div>
                            </div>
                          </>
                        )}
                        {!elpaketDispatcherSelected && pickupSelected && externalServices[0]?.pickUpService && (
                          <>
                            <div className={styles.costItem}>
                              <span>{externalServices[0].pickUpService.serviceName}</span>
                              <span>{externalServices[0].pickUpService.serviceSubTotal.toFixed(2)} {currentSelectedRate.currency}</span>
                            </div>
                            <div className={styles.pickupNotice}>
                              <div>Fecha de recogida: <strong>{values.pickupDate}</strong></div>
                              <div>Horario de recogida: entre las <strong>8:00</strong> y las <strong>17:00</strong></div>
                            </div>
                          </>
                        )}
                        {!elpaketDispatcherSelected && !pickupSelected && (externalServices[0]?.pickUpService || externalServices[0]?.elpaketDispatcherService) && (
                          <div className={styles.costItem}>
                            <span>DROP OFF</span>
                            <span>0.00 {currentSelectedRate.currency}</span>
                          </div>
                        )}
                        <div
                          className={`${styles.costItem} ${styles.totalCost}`}
                        >
                          <span>TOTAL A PAGAR</span>
                          <span>
                            {dynamicTotal.toFixed(2)}{" "}
                            {currentSelectedRate.currency}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className={styles.stepActionsSummary}>
                      <button
                        className={styles.cancelShipmentBtn}
                        onClick={() => setCurrentStep(4)}
                      >
                        Atrás
                      </button>
                      <button
                        className={styles.processShipmentBtn}
                        onClick={handleNextStep}
                      >
                        Siguiente <FontAwesomeIcon icon={faChevronRight} />
                      </button>
                    </div>
                    <div className={styles.shippingIllustrationContainer}>
                      <img
                        src={cajaTransparente}
                        alt="Caja Elpaket"
                        className={styles.shippingIllustration}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            {orderSuccessData ? (
              <div className={styles.sendPackageContent}>
                <OrderSuccessCard
                  trackingNumber={orderSuccessData.trackingNumber}
                  upsTrackingNumber={orderSuccessData.externalService?.find(
                    (s: any) => s.providerName?.toUpperCase() === "UPS" || s.serviceName?.toUpperCase() === "UPS"
                  )?.serviceTrackingNumber}
                  carrierName={orderSuccessData.carrierName}
                  receiverName={`${values.receiverFirstName} ${values.receiverLastName}`}
                  destination={`${values.receiverCity}, ${values.receiverState}, VE`}
                  weight={`${values.weight} ${values.unitSystem === "imperial" ? "lb" : "kg"}`}
                  content={values.packageContent}
                  carrierBase64={orderSuccessData.carrierBase64}
                  base64Pdf={orderSuccessData.base64Pdf}
                  idOrder={orderSuccessData.id || orderSuccessData.idOrder}
                  onNewShipment={() => window.location.reload()}
                />
              </div>
            ) : (
              currentStep === 6 &&
              currentSelectedRate && (
                <div className={`${styles.sendPackageSection} fade-in`}>
                  <div className={styles.paymentStepContainer}>
                    {/* Left Column: Selection and Details */}
                    <div className={styles.paymentLeftCol}>
                      <div className={styles.paymentDetailsCard}>
                        {loadingPayment ? (
                          <div className={styles.paymentLoading}>
                            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                            <p>Cargando plataforma de pago...</p>
                          </div>
                        ) : stripeError ? (
                          <div className="stripe-error-container" style={{ padding: '20px', textAlign: 'center', color: '#721c24', backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '8px' }}>
                            <FontAwesomeIcon icon={faShieldAlt} size="2x" style={{ marginBottom: '10px' }} />
                            <p style={{ fontWeight: 'bold' }}>No se pudo cargar la plataforma de pago.</p>
                            <p>Por favor, comprueba tu conexión a internet o desactiva bloqueadores de anuncios y recarga la página.</p>
                            <button
                              onClick={() => window.location.reload()}
                              className="process-shipment-btn"
                              style={{ marginTop: '15px', padding: '8px 16px', fontSize: '14px' }}
                            >
                              Reintentar
                            </button>
                          </div>
                        ) : clientSecret ? (
                          <Elements
                            stripe={stripePromise}
                            options={{
                              clientSecret,
                              paymentMethodCreation: "manual",
                            }}
                          >
                            <StripePaymentForm
                              amount={dynamicTotal}
                              currency={currentSelectedRate?.currency || "USD"}
                              onSuccess={async (methodId) => {
                                const result = await Swal.fire({
                                  title: "¿Confirmar pago?",
                                  text: `¿Estás seguro de que deseas procesar el pago de ${dynamicTotal.toFixed(2)} ${currentSelectedRate?.currency || "USD"} y crear el envío?`,
                                  icon: "question",
                                  showCancelButton: true,
                                  confirmButtonColor: "#10b981",
                                  cancelButtonColor: "#d33",
                                  confirmButtonText: "Sí, pagar",
                                  cancelButtonText: "Cancelar",
                                });

                                if (result.isConfirmed) {
                                  executeShipmentCreation(methodId);
                                }
                              }}
                              onError={(msg) => {
                                console.error("Stripe error:", msg);
                              }}
                            />
                          </Elements>
                        ) : (
                          <div className={styles.paymentError}>
                            <p>No se pudo cargar la pasarela de pago.</p>
                            <button
                              type="button"
                              className={styles.cancelShipmentBtn}
                              onClick={() =>
                                fetchPaymentIntent(
                                  dynamicTotal,
                                  currentSelectedRate?.currency || "USD",
                                )
                              }
                            >
                              Reintentar
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right Column: Total and Actions */}
                    <div className={styles.paymentRightCol}>
                      <div className={styles.paymentSummaryTotalCard}>
                        <span className={styles.paymentTotalLabel}>
                          TOTAL A PAGAR
                        </span>
                        <div className={styles.paymentTotalAmount}>
                          {dynamicTotal.toFixed(2)}{" "}
                          {currentSelectedRate.currency}
                        </div>
                      </div>

                      <div className={styles.securityMessage}>
                        <FontAwesomeIcon icon={faShieldAlt} />
                        <span>Tus datos están seguros y encriptados</span>
                      </div>

                      <div className={styles.paymentActions}>
                        <button
                          type="button"
                          className={styles.cancelShipmentBtn}
                          onClick={handleBackStep}
                          disabled={isProcessingOrder}
                        >
                          <FontAwesomeIcon icon={faChevronLeft} /> Atrás
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )
            )}
            {currentStep === 1 && (
              <div className={`${styles.sendPackageSection} fade-in`}>
                <div className={styles.summaryHeaderFlex}>
                  <h4 className={styles.m0}>Datos del Paquete</h4>
                  <div className={styles.unitToggle} style={{ margin: 0 }}>
                    <button
                      className={`${styles.toggleBtn} ${values.unitSystem === "imperial" ? styles.active : ""}`}
                      onClick={() => {
                        setValues((prev: any) => ({
                          ...prev,
                          unitSystem: "imperial",
                          width: "",
                          height: "",
                          length: "",
                          weight: "",
                        }));
                        setErrors((prev: any) => ({
                          ...prev,
                          width: undefined,
                          height: undefined,
                          length: undefined,
                          weight: undefined,
                        }));
                        setDisclaimerError(false);
                      }}
                    >
                      Imperial (lb/in)
                    </button>
                    <button
                      className={`${styles.toggleBtn} ${values.unitSystem === "metric" ? styles.active : ""}`}
                      onClick={() => {
                        setValues((prev: any) => ({
                          ...prev,
                          unitSystem: "metric",
                          width: "",
                          height: "",
                          length: "",
                          weight: "",
                        }));
                        setErrors((prev: any) => ({
                          ...prev,
                          width: undefined,
                          height: undefined,
                          length: undefined,
                          weight: undefined,
                        }));
                        setDisclaimerError(false);
                      }}
                    >
                      Métrico (kg/cm)
                    </button>
                  </div>
                </div>

                <div className={styles.packageInfoGrid}>
                  <div className={styles.stepImageCentered}>
                    <BoxIllustration className={styles.stepImageLarge} />
                  </div>

                  <div className={styles.packageInfoInputs}>
                    <div className={styles.formGroup}>
                      <label>Tipo de Paquete</label>
                      <div style={{ display: 'flex', gap: '12px', marginTop: '5px' }}>
                        {/* Option: Paquete */}
                        <div
                          style={{
                            flex: 1,
                            padding: '12px 16px',
                            borderRadius: '12px',
                            border: '1.5px solid #e2e8f0',
                            backgroundColor: '#ffffff',
                            boxShadow: 'none',
                            transition: 'all 0.25s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}
                          onClick={() => {
                            setValues((prev: any) => ({
                              ...prev,
                              parcelOrdDoc: 'PARCEL',
                              width: '',
                              height: '',
                              length: '',
                              weight: '',
                              fob: '',
                              packageContent: '',
                              hasBattery: false,
                            }));
                            setSelectedDocument(null);
                            setDisclaimerChecked(false);
                            setErrors({});
                            setDisclaimerError(false);
                          }}
                        >
                          <input
                            type="checkbox"
                            name="parcelOrdDoc"
                            checked={values.parcelOrdDoc === 'PARCEL'}
                            onChange={() => { }}
                            style={{
                              width: '18px',
                              height: '18px',
                              cursor: 'pointer'
                            }}
                          />
                          <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#475569' }}>
                            Caja
                          </span>
                        </div>

                        {/* Option: Documento */}
                        <div
                          style={{
                            flex: 1,
                            padding: '12px 16px',
                            borderRadius: '12px',
                            border: '1.5px solid #e2e8f0',
                            backgroundColor: '#ffffff',
                            boxShadow: 'none',
                            transition: 'all 0.25s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}
                          onClick={() => {
                            setValues((prev: any) => ({
                              ...prev,
                              parcelOrdDoc: 'DOC',
                              width: '',
                              height: '',
                              length: '',
                              weight: '',
                              fob: '',
                              packageContent: '',
                              hasBattery: false,
                            }));
                            setSelectedDocument(null);
                            setDisclaimerChecked(false);
                            setErrors({});
                            setDisclaimerError(false);
                          }}
                        >
                          <input
                            type="checkbox"
                            name="parcelOrdDoc"
                            checked={values.parcelOrdDoc === 'DOC'}
                            onChange={() => { }}
                            style={{
                              width: '18px',
                              height: '18px',
                              cursor: 'pointer'
                            }}
                          />
                          <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#475569' }}>
                            Documento
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className={`${styles.inputGroupGrid3Col} ${styles.mt15}`}>
                      <div className={styles.formGroup}>
                        <label>
                          Largo ({values.unitSystem === "metric" ? "cm" : "in"})
                        </label>
                        <input
                          type="number"
                          name="length"
                          value={values.length}
                          onChange={handleChange}
                          className={errors.length ? styles.isInvalid : ""}
                          placeholder="0"
                        />
                        {errors.length && (
                          <span className={styles.invalidFeedback}>
                            {errors.length}
                          </span>
                        )}
                      </div>
                      <div className={styles.formGroup}>
                        <label>
                          Ancho ({values.unitSystem === "metric" ? "cm" : "in"})
                        </label>
                        <input
                          type="number"
                          name="width"
                          value={values.width}
                          onChange={handleChange}
                          className={errors.width ? styles.isInvalid : ""}
                          placeholder="0"
                        />
                        {errors.width && (
                          <span className={styles.invalidFeedback}>
                            {errors.width}
                          </span>
                        )}
                      </div>
                      <div className={styles.formGroup}>
                        <label>
                          Alto ({values.unitSystem === "metric" ? "cm" : "in"})
                        </label>
                        <input
                          type="number"
                          name="height"
                          value={values.height}
                          onChange={handleChange}
                          className={errors.height ? styles.isInvalid : ""}
                          placeholder="0"
                        />
                        {errors.height && (
                          <span className={styles.invalidFeedback}>
                            {errors.height}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className={`${styles.inputGroupGrid2Col} ${styles.mt15}`}>
                      <div className={styles.formGroup}>
                        <label>
                          Peso ({values.unitSystem === "metric" ? "kg" : "lb"})
                        </label>
                        <input
                          type="number"
                          name="weight"
                          value={values.weight}
                          onChange={handleChange}
                          className={errors.weight ? styles.isInvalid : ""}
                          placeholder="0.00"
                        />
                        {errors.weight && (
                          <span className={styles.invalidFeedback}>
                            {errors.weight}
                          </span>
                        )}
                      </div>
                      <div className={styles.formGroup}>
                        <label>Valor FOB (USD)</label>
                        <input
                          type="number"
                          name="fob"
                          value={values.fob}
                          onChange={handleChange}
                          className={errors.fob ? styles.isInvalid : ""}
                          placeholder="0.00"
                        />
                        {errors.fob && (
                          <span className={styles.invalidFeedback}>
                            {errors.fob}
                          </span>
                        )}
                      </div>
                    </div>


                    <div className={`${styles.formGroup} ${styles.mt20}`}>
                      <label>Descripción del Contenido *</label>
                      <textarea
                        name="packageContent"
                        value={values.packageContent}
                        onChange={handleChange}
                        className={errors.packageContent ? styles.isInvalid : ""}
                        placeholder="Ej: Ropa, repuestos, electrónicos..."
                        rows={3}
                      />
                      {errors.packageContent && (
                        <span className={styles.invalidFeedback}>
                          {errors.packageContent}
                        </span>
                      )}
                    </div>

                      <div id="doc-upload-container" className={`${styles.formGroup} ${styles.mt20}`} style={{ border: '1px dashed #cbd5e1', borderRadius: '12px', padding: '16px', backgroundColor: '#f8fafc' }}>
                        <label style={{ fontWeight: 600, color: '#334155', display: 'block', marginBottom: '8px' }}>
                          Documentación de Respaldo (Opcional)
                        </label>
                        <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '12px' }}>
                          Cargue un archivo de respaldo (PNG/JPG máx 5MB, PDF máx 500KB). Si dispone de documentación de respaldo, se recomienda adjuntarla.
                        </p>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                          <label style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '8px 16px',
                            backgroundColor: '#0f172a',
                            color: '#ffffff',
                            borderRadius: '8px',
                            cursor: (selectedDocument || isCompressingDoc) ? 'not-allowed' : 'pointer',
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            opacity: (selectedDocument || isCompressingDoc) ? 0.6 : 1,
                            userSelect: 'none',
                            transition: 'background-color 0.2s'
                          }}>
                            <FontAwesomeIcon icon={isCompressingDoc ? faSpinner : faPlus} spin={isCompressingDoc} style={{ marginRight: '6px' }} />
                            {isCompressingDoc ? "Procesando..." : "Seleccionar Archivo"}
                            <input
                              type="file"
                              accept=".png,.jpg,.jpeg,.pdf"
                              onChange={handleDocumentChange}
                              disabled={!!selectedDocument || isCompressingDoc}
                              style={{ display: 'none' }}
                            />
                          </label>
                          <span style={{ fontSize: '0.85rem', color: '#475569' }}>
                            {isCompressingDoc ? "Comprimiendo archivo..." : selectedDocument ? "1 de 1 archivo cargado" : "0 de 1 archivo cargado"}
                          </span>
                        </div>

                        {docErrors && (
                          <div style={{ color: '#ef4444', fontSize: '0.85rem', marginTop: '8px', fontWeight: 500 }}>
                            {docErrors}
                          </div>
                        )}

                        {selectedDocument && (
                          <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '8px 12px',
                              backgroundColor: '#ffffff',
                              border: '1px solid #e2e8f0',
                              borderRadius: '8px',
                              fontSize: '0.85rem'
                            }}>
                              <span style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '80%', color: '#334155', fontWeight: 500 }}>
                                {selectedDocument.file.name} ({(selectedDocument.file.size / (1024 * 1024)).toFixed(2)} MB)
                              </span>
                              <button
                                type="button"
                                onClick={removeDocument}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#ef4444',
                                  cursor: 'pointer',
                                  fontWeight: 'bold',
                                  padding: '2px 6px',
                                  fontSize: '0.9rem'
                                }}
                              >
                                &times;
                              </button>
                            </div>
                          </div>
                        )}
                      </div>


                    <div
                      style={{
                        marginTop: '15px',
                        padding: '12px 16px',
                        borderRadius: '12px',
                        border: '1.5px solid #e2e8f0',
                        backgroundColor: '#ffffff',
                        boxShadow: 'none',
                        transition: 'all 0.25s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        cursor: values.parcelOrdDoc === 'DOC' ? 'not-allowed' : 'pointer',
                        opacity: values.parcelOrdDoc === 'DOC' ? 0.6 : 1,
                        userSelect: 'none'
                      }}
                      onClick={() => {
                        if (values.parcelOrdDoc !== 'DOC') {
                          setValues((prev: any) => ({ ...prev, hasBattery: !prev.hasBattery }));
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        name="hasBattery"
                        checked={values.hasBattery}
                        disabled={values.parcelOrdDoc === 'DOC'}
                        onChange={(e) => {
                          e.stopPropagation();
                          if (values.parcelOrdDoc !== 'DOC') {
                            setValues((prev: any) => ({ ...prev, hasBattery: e.target.checked }));
                          }
                        }}
                        style={{
                          width: '18px',
                          height: '18px',
                          minWidth: '18px',
                          cursor: values.parcelOrdDoc === 'DOC' ? 'not-allowed' : 'pointer'
                        }}
                      />
                      <span style={{ fontSize: '13.5px', fontWeight: 500, color: '#475569', userSelect: 'none', lineHeight: '1.5' }}>
                        El paquete contiene baterías (ej. Laptops, teléfonos, etc.)
                      </span>
                    </div>
                    <div
                      id="disclaimer-checkbox-container"
                      style={{
                        marginTop: '20px',
                        padding: '16px',
                        borderRadius: '12px',
                        border: disclaimerError ? '1.5px solid #ef4444' : '1.5px solid #e2e8f0',
                        backgroundColor: '#ffffff',
                        boxShadow: 'none',
                        transition: 'all 0.25s ease',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        const nextVal = !disclaimerChecked;
                        setDisclaimerChecked(nextVal);
                        if (nextVal) setDisclaimerError(false);
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={disclaimerChecked}
                        onChange={(e) => {
                          e.stopPropagation();
                          setDisclaimerChecked(e.target.checked);
                          if (e.target.checked) setDisclaimerError(false);
                        }}
                        style={{
                          width: '18px',
                          height: '18px',
                          minWidth: '18px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          marginTop: '2px',
                          accentColor: 'var(--brand-black)'
                        }}
                      />
                      <span style={{
                        fontSize: '13.5px',
                        color: '#475569',
                        lineHeight: '1.5',
                        userSelect: 'none',
                        fontWeight: 500
                      }}>
                        Confirmo que las dimensiones y el peso ingresados son verídicos. Entiendo que están sujetos a recálculo y auditoría por parte del courier final.
                      </span>
                    </div>
                    {disclaimerError && (
                      <span style={{ display: 'block', marginTop: '6px', paddingLeft: '4px', color: '#ef4444', fontSize: '12px', fontWeight: 500 }}>
                        Debes confirmar las dimensiones y el peso para continuar.
                      </span>
                    )}
                  </div>
                </div>

                <div className={`${styles.stepActions} ${styles.mt30}`}>
                  <button
                    className={styles.processShipmentBtn}
                    onClick={handleNextStep}
                  >
                    Siguiente <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {selectedRateForModal && (
          <ServiceDetailModalV2
            show={showServiceDetailModal}
            onHide={() => {
              setShowServiceDetailModal(false);
              setSelectedRateForModal(null);
            }}
            rateOption={selectedRateForModal}
            selectedExternalIndices={
              selectedExternalIndicesByCarrier.get(
                getServiceKey(selectedRateForModal),
              ) || new Set()
            }
            onSelectExternalService={(newIndices) =>
              selectExternalService(selectedRateForModal, newIndices)
            }
          />
        )}

        <AuthPickupModal
          show={showAuthModal}
          isExistingUser={emailExists === true}
          userName={
            emailExists && existingCustomerName
              ? existingCustomerName
              : (values.isThirdPerson
                ? `${values.userFirstName} ${values.userLastName}`.trim()
                : `${values.senderFirstName} ${values.senderLastName}`.trim())
          }
          userEmail={values.isThirdPerson ? values.userEmail : values.senderEmail}
          onConfirm={async (password) => {
            await performAuth(password);
          }}
          onCancel={() => {
            setShowAuthModal(false);
          }}
        />
        <EditRawAddressModal
          isOpen={showEditSenderModal}
          onClose={() => setShowEditSenderModal(false)}
          onSave={handleSaveSenderData}
          initialData={{
            firstName: values.senderFirstName,
            lastName: values.senderLastName,
            email: values.senderEmail,
            phone: values.senderPhone,
            kindOfDNI: values.senderKindOfDNI,
            dni: values.senderDNI,
            addressLine1: values.senderAddressLine1,
            addressLine2: values.senderAddressLine2 || "",
            city: values.senderCity,
            state: values.senderState,
            zipCode: values.originZip,
            country: values.origin,
          }}
          title="Editar Remitente"
          showPersonalInfo={values.isThirdPerson}
          phoneCode={selectedSenderCountry?.phoneCode}
        />

        <EditRawAddressModal
          isOpen={showEditReceiverModal}
          onClose={() => setShowEditReceiverModal(false)}
          onSave={handleSaveReceiverData}
          initialData={{
            firstName: values.receiverFirstName,
            lastName: values.receiverLastName,
            email: values.receiverEmail,
            phone: values.receiverPhone,
            kindOfDNI: values.receiverKindOfDNI,
            dni: values.receiverDNI,
            addressLine1: values.receiverAddressLine1,
            addressLine2: values.receiverAddressLine2 || "",
            city: values.receiverCity,
            state: values.receiverState,
            zipCode: values.destinationZip,
            country: values.destination,
          }}
          title="Editar Destinatario"
          showPersonalInfo={true}
          phoneCode={selectedReceiverCountry?.phoneCode}
        />

        <EditPackageDataModal
          isOpen={showEditPackageModal}
          onClose={() => setShowEditPackageModal(false)}
          onSave={handleSavePackageData}
          initialData={{
            width: values.width,
            height: values.height,
            length: values.length,
            weight: values.weight,
            fob: values.fob,
            unitSystem: values.unitSystem,
            parcelOrdDoc: values.parcelOrdDoc,
            currency: values.currency,
            packageContent: values.packageContent,
            pickupDate: values.pickupDate,
            hasBattery: values.hasBattery,
            selectedDocument: selectedDocument,
          }}
          currencies={currencies}
          isDropOff={!pickupSelected && !elpaketDispatcherSelected}
        />
      </main>

      <Footer />
    </div>
  );
};

export default PickUpPage;
